/****************
 * CopyCat Backend API Client
 * Handles REST API communication with stats-server
 ****************/

import { inSpeedRun, speedRunStartedAt } from '@/lib/speedRun';
import { newcomerLens, recordRunCreation, rememberRunFavorite } from '@/lib/speedRunLens.core';
import { track } from '@/lib/analytics';
import { ENGAGEMENT_PATHS, type TraderPicks, type Visit, type Recap, type MilestoneState, type QuizState } from '@/lib/engagement.core';
import { makeBacktestApi, USER_BACKTESTS } from '@/lib/backtest.core';
import { type AssetFilter, assetFilterQuery } from '@/lib/instruments';
import { recordListTruncation } from '@/lib/listTruncation';
import i18n from '@/i18n';
import { closeSideQuery } from '@/lib/closePosition';
import { getViewAsUserId, ReadOnlyViewError } from '@/lib/viewAs';
import type { SessionWalletHistory } from '@/lib/keyWalletCurve';
import type { RegimeLiveResponse } from '@/lib/regimeWidget';

const API_BASE = import.meta.env.VITE_API_URL || '';

/****************
 * Types for API responses
 ****************/

export interface StatsResponse {
  metric_name: string;
  horizon: string;
  data: unknown;
  timestamp: number;
  watermark: WatermarkData;
}

export interface BulkStatsResponse {
  horizon: string;
  statistics: Record<string, unknown>;
  watermark: WatermarkData;
  timestamp: number;
}

export interface SummaryResponse {
  summary: Record<string, HorizonData>;
  timestamp: number;
}

export interface HorizonData {
  data: Record<string, unknown>;
  watermark: WatermarkData;
}

export interface WatermarkData {
  timestamp: string;
  id: number;
}

export interface HealthResponse {
  status: string;
  timestamp: number;
  healthy?: boolean;
}

export interface MetricsResponse {
  state_manager: unknown;
  bridge: unknown;
  timestamp: number;
}

/****************
 * API Error class
 ****************/

export class ApiError extends Error {
  status: number;
  /****************
   * Did OUR server answer, or did something in front of it?
   *
   * Our handlers always reply {"error":"<sentence>"}. A body we cannot parse as
   * that came from somewhere else — a Cloudflare 502 page, a proxy, a dead
   * connection — and it means we do not know what the request DID, only that we
   * never heard back.
   *
   * For a read that distinction is cosmetic. For a close it is the whole thing:
   * the order may already be at the exchange, and "we could not reach the
   * server" must never be reported as "nothing happened".
   ****************/
  answeredByServer: boolean;
  /** A short code some handlers send beside the sentence ({"reason":"code_wrong"}),
   *  so a page can say it in the reader's language. */
  reason?: string;

  constructor(message: string, status: number, answeredByServer = true, reason?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.answeredByServer = answeredByServer;
    this.reason = reason;
  }
}

/****************
 * Fetch wrapper with error handling
 ****************/

type FetchOptions = RequestInit & { onResponse?: (r: Response) => void };

async function fetchApi<T>(endpoint: string, options?: FetchOptions): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options?.headers as Record<string, string>),
  };

  /*
   * Impersonation, and the one place it is enforced.
   *
   * When this document was opened with ?viewAs=<id> it is showing somebody
   * else's account, so every request identifies as them. The write guard sits
   * HERE rather than in the components: hiding a Stop button is a promise, a
   * refusal at the only door every call goes through is a guarantee, and the
   * failure being guarded is stopping a live session or opening a trade on
   * another person's money. See lib/viewAs.ts.
   */
  const viewAs = getViewAsUserId();
  if (viewAs) {
    const method = (options?.method || 'GET').toUpperCase();
    if (method !== 'GET' && method !== 'HEAD') {
      throw new ReadOnlyViewError(endpoint);
    }
  }

  const userId = viewAs || (window as any).__clerk_user_id;
  // An admin's speed run: the Orb talks to a newcomer, in its own conversation
  // (backend assistant_speed_run.go).
  const runStart = viewAs ? null : speedRunStartedAt();
  if (runStart) headers['X-Speed-Run'] = runStart.toISOString();
  if (userId) {
    headers['X-User-ID'] = userId;
  } else if (!endpoint.startsWith('/api/v1/dashboard/')) {
    /* A request that identifies a user, sent without identifying them.
       The backend does not reject it -- it falls back to "dev-user"
       (handlers.go:72) and answers with that account's data, which is empty.
       React Query then caches an EMPTY LIST AS A SUCCESS: no error, no
       spinner, no retry, just zeros that look exactly like a new account.

       That silence cost three rounds of debugging on 2026-09-03. It is a
       warning rather than a thrown error because throwing would break the
       signed-out and demo paths that legitimately have no id -- but it must
       never again happen without saying so. */
    console.warn(
      `[api] ${endpoint} sent with no X-User-ID — the server will answer as "dev-user" ` +
        `and this response will be empty. If you are signed in, this is a bug.`,
    );
  }

  /****************
   * NO ANSWER AT ALL: THE PHONE'S WORDS, IN THE READER'S LANGUAGE.
   *
   * A fetch that throws never reached CopyFi (offline, DNS, a dropped
   * connection). It used to escape as the browser's own TypeError, "Failed to
   * fetch", in English whatever the reader's language. Now it is the phone's
   * ApiError for the same failure: status 0, nothing heard back. A cancel
   * (AbortError) is not a failure and goes through untouched.
   ****************/
  let response: Response;
  try {
    response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers,
    });
  } catch (e) {
    if (e instanceof Error && e.name === 'AbortError') throw e;
    throw new ApiError(i18n.t('mobile:api.unreachable'), 0, false);
  }

  /* Some endpoints carry meaning on RESPONSE HEADERS rather than in the body:
     a trader's trades send X-Total-Count (the count BEFORE the limit) and
     X-Next-Cursor. The body stays a bare array so nothing that already decodes
     it breaks, which means the only way to read the rest is to keep the
     Response. onResponse gets it before the body is consumed. 2026-09-08. */
  options?.onResponse?.(response);

  /* And the headers that say the list was CUT. Five endpoints have set these
     since 2026-09-19 and nothing read them, so "All Orders (500)" on an
     account with 1,300 orders looked like all of them. lib/listTruncation.ts.
     M-50, 2026-09-22. */
  recordListTruncation(endpoint, response);

  if (!response.ok) {
    const errorText = await response.text().catch(() => 'Unknown error');
    // The backend returns errors as {"error":"<human message>"}. Surface that
    // sentence directly (e.g. the "assets not available on Phemex" explanation)
    // instead of dumping the raw JSON with braces/quotes into a toast.
    let message = errorText;
    let answeredByServer = false;
    let reason: string | undefined;
    try {
      const parsed = JSON.parse(errorText);
      if (parsed && typeof parsed.error === 'string') {
        message = parsed.error;
        answeredByServer = true;
        if (typeof parsed.reason === 'string') reason = parsed.reason;
      }
    } catch {
      /****************
       * NOT JSON — SO IT IS NOT OURS, AND THE RAW TEXT MUST NOT BE SHOWN.
       *
       * This used the body as the message. A Cloudflare 502 is a full HTML
       * page, so the close dialog put several hundred lines of markup in a
       * toast — Guillaume saw exactly that. The status is the only thing worth
       * saying, because nothing in that page is about this request.
       *
       * Where the phone has words for the same failure they are used, so the
       * web's message is translated too: a gateway that gave up waiting is a
       * timeout, one that could not reach us is "could not reach CopyFi", and
       * a sign-in refused by something in front of us is an expired session.
       ****************/
      message =
        response.status === 504
          ? i18n.t('mobile:api.timeout')
          : response.status === 502 || response.status === 503
            ? i18n.t('mobile:api.unreachable')
            : response.status === 401 || response.status === 403
              ? i18n.t('mobile:queryState.sessionExpired')
              : i18n.t('common:api.requestFailed', { status: response.status });
    }
    // api_error_shown (2026-10-22): an error came back to a screen. Never for the
    // analytics endpoint itself, which would feed on its own failures.
    if (!endpoint.includes('/analytics')) track('api_error_shown', String(message).slice(0, 200), undefined, { endpoint: endpoint.split('?')[0].slice(0, 120), status: response.status });
    throw new ApiError(message, response.status, answeredByServer, reason);
  }

  const body = await response.json();
  // What the run creates is written down: leaving it deletes only that (lib/speedRunLens.core.ts).
  if (runStart) recordRunCreation(options?.method, endpoint, body);
  // A speed run sees none of the real account (lib/speedRunLens.ts).
  if (runStart && (options?.method || 'GET').toUpperCase() === 'GET') {
    return newcomerLens(endpoint, body, (e) => fetchRaw(e, headers)) as Promise<T>;
  }
  return body;
}

/** A plain GET with the same headers, outside the lens (the lens reads the run's own sessions with it). */
async function fetchRaw(endpoint: string, headers: Record<string, string>): Promise<any> {
  const r = await fetch(`${API_BASE}${endpoint}`, { headers });
  if (!r.ok) throw new Error(`${endpoint}: ${r.status}`);
  return r.json();
}

/****************
 * Stats API methods
 ****************/

export const statsApi = {
  getSummary: (): Promise<SummaryResponse> =>
    fetchApi('/api/v1/stats/summary'),

  getBulkStats: (horizon: string): Promise<BulkStatsResponse> =>
    fetchApi(`/api/v1/stats/bulk/${horizon}`),

  getStatistic: (metric: string, horizon: string): Promise<StatsResponse> =>
    fetchApi(`/api/v1/stats/${metric}/${horizon}`),

  getWatermarks: (): Promise<{ watermarks: Record<string, WatermarkData>; timestamp: number }> =>
    fetchApi('/api/v1/stats/watermarks'),

  getWatermark: (horizon: string): Promise<{ horizon: string; watermark: WatermarkData; timestamp: number }> =>
    fetchApi(`/api/v1/stats/watermarks/${horizon}`),
};

/****************
 * Health API methods
 ****************/

/****************
 * TOKENS FOR THE USER'S OWN AI.
 *
 * Through fetchApi like everything else, so these inherit the identity header
 * and — more importantly — the impersonation write-guard above. An admin
 * looking at somebody's screens must not be able to mint a credential to their
 * account, and that refusal already lives at the one door every call passes
 * through rather than needing a second one here.
 ****************/
export interface MCPTokenRow {
  id: number;
  prefix: string;
  label: string;
  created_at: string;
  last_used_at?: string | null;
  revoked_at?: string | null;
  /** 'oauth' for an app signed in by OAuth (ChatGPT, claude.ai, the Gemini app); absent for a token. */
  kind?: 'oauth';
  scope?: string;
}

export const mcpApi = {
  list: (): Promise<{ tokens: MCPTokenRow[] }> =>
    fetchApi('/api/v1/mcp/tokens'),

  // The token comes back exactly once. Nothing here stores it: it goes to the
  // screen, and from there to the user's clipboard or nowhere.
  /* scope 'trade' MUST name a session: an account-wide trade token is refused
     when used, so issuing one would hand the user a credential that cannot do
     anything. The server enforces that; this signature mirrors it. */
  create: (
    label: string,
    opts?: { sessionId?: number; scope?: 'read' | 'trade' },
  ): Promise<{ token: string; prefix: string; label: string; notice: string }> =>
    fetchApi('/api/v1/mcp/tokens', {
      method: 'POST',
      body: JSON.stringify({
        label,
        ...(opts?.sessionId ? { session_id: opts.sessionId } : {}),
        ...(opts?.scope ? { scope: opts.scope } : {}),
      }),
    }),

  revoke: (id: number, kind?: 'oauth'): Promise<{ revoked: number }> =>
    fetchApi('/api/v1/mcp/tokens/revoke', { method: 'POST', body: JSON.stringify({ id, ...(kind ? { kind } : {}) }) }),
};

/* A session's trader interview, the AI's notes, and the chat with a
   pay-per-decision model (backend rest/ai_session_chat.go). */
export interface AISessionChatState {
  messages: { id: number; role: 'user' | 'assistant'; content: string; created_at: string }[];
  preferences: Record<string, unknown> | null;
  directives: string;
  notes: string;
  questions: { field: string; ask: string }[];
  model_ready: boolean;
  way_b: boolean;
}
export const aiSessionApi = {
  get: (sessionId: number): Promise<AISessionChatState> => fetchApi(`/api/v1/sessions/${sessionId}/ai-chat`),
  send: (sessionId: number, message: string, study = false): Promise<{ reply: string; save_problem?: string }> =>
    fetchApi(`/api/v1/sessions/${sessionId}/ai-chat`, { method: 'POST', body: JSON.stringify({ message, study }) }),
};

/* The feature catalogue (backend rest/feature_catalogue.go). */
export interface FeatureSeries { name: string; description: string; per_symbol: boolean; period: string; known_at: string; source: string; available: boolean }
export interface FeatureSelectionRow { feature: string; symbol: string }
export const featureApi = {
  catalogue: (q = '', offset = 0): Promise<{ series: FeatureSeries[]; total: number; available: number; limit: number; selection: FeatureSelectionRow[] }> =>
    fetchApi(`/api/v1/features/catalogue?q=${encodeURIComponent(q)}&offset=${offset}`),
  /* The picked features' history as one CSV file, saved by the browser. */
  /* opts.start: YYYY-MM-DD, the history from that day; opts.update: only what is new since the last send (2026-10-24). */
  download: async (opts: { start?: string; update?: boolean } = {}): Promise<void> => {
    const userId = (window as any).__clerk_user_id;
    const qs = opts.update ? '?update=1' : opts.start ? `?start=${encodeURIComponent(opts.start)}` : '';
    const res = await fetch(`${API_BASE}/api/v1/features/download${qs}`, { headers: userId ? { 'X-User-ID': userId } : {} });
    if (!res.ok) {
      let msg = `${res.status}`;
      try { msg = (await res.json()).error ?? msg; } catch { /* not JSON */ }
      throw new Error(msg);
    }
    const blob = await res.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `copyfi-features-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  },
  select: (items: FeatureSelectionRow[]): Promise<{ selection: FeatureSelectionRow[] }> =>
    fetchApi('/api/v1/features/selection', { method: 'PUT', body: JSON.stringify({ items }) }),
};

/* The OAuth consent page (pages/OAuthConsent.tsx, backend rest/mcp_oauth.go).
   approve carries the Clerk session token: the server takes the account from
   it, never from X-User-ID. */
export const oauthApi = {
  client: (params: string): Promise<{ client_name: string; redirect_host: string; trade_requested: boolean }> =>
    fetchApi(`/api/v1/oauth/client?${params}`),
  approve: (
    sessionToken: string,
    body: { params: string; approve: boolean; scope?: 'read' | 'trade'; session_id?: number },
  ): Promise<{ redirect: string }> =>
    fetchApi('/api/v1/oauth/approve', {
      method: 'POST',
      headers: { Authorization: `Bearer ${sessionToken}` },
      body: JSON.stringify(body),
    }),
};

/**
 * The account's tier (lib/tiers.ts). `enforced` is false until the paywall is
 * switched on; until then nothing is locked whatever `tier` says.
 */
export interface Entitlements {
  tier: string;
  enforced: boolean;
  orb_daily_questions: number | null;
}

export const entitlementsApi = {
  get: (): Promise<Entitlements> => fetchApi('/api/v1/me/entitlements'),
};

/** The Profile pop-up and referrals (backend referral.go). */
export interface ProfileStats {
  sessions: number; running: number; live_sessions: number; paper_sessions: number; signal_sessions: number;
  closed_trades: number; wins: number; net_pnl_usd: number; live_net_pnl_usd: number;
}
export interface MyProfile {
  tier: string;
  signed_up?: string;
  handle?: string;
  stats?: ProfileStats;
  stats_error?: string;
  referral: {
    code: string;
    link: string;
    referred_by?: { name: string; since: string };
    people: { name: string; since: string; signed_up?: string; tier: string }[];
  };
}
export interface ReferralRow { user_id: string; referrer_id: string; source: string; set_by: string; created_at: string }
export interface ReferralChangeRow { id: number; user_id: string; old_referrer: string; new_referrer: string; changed_by: string; reason: string; created_at: string }

export const profileApi = {
  get: (): Promise<MyProfile> => fetchApi('/api/v1/me/profile'),
  /** Emails a 6-digit code for deleting this account (backend account_delete.go). */
  sendDeleteCode: (): Promise<{ sent_to: string }> => fetchApi('/api/v1/me/account/delete-code', { method: 'POST' }),
  /** Deletes this account and its data. Needs the emailed code; 409 while live sessions run. */
  deleteAccount: (code: string, reason: string): Promise<{ deleted: boolean }> =>
    fetchApi('/api/v1/me/account', { method: 'DELETE', body: JSON.stringify({ code, reason }) }),
  claimReferral: (code: string, source: 'link' | 'code'): Promise<{ claimed: boolean }> =>
    fetchApi('/api/v1/me/referral/claim', { method: 'POST', body: JSON.stringify({ code, source }) }),
  adminReferrals: (): Promise<{ referrals: ReferralRow[]; changes: ReferralChangeRow[] }> => fetchApi('/api/v1/admin/referrals'),
  adminSetReferral: (userId: string, referrerId: string, reason: string, admin: string): Promise<unknown> =>
    fetchApi('/api/v1/admin/referral', { method: 'POST', body: JSON.stringify({ user_id: userId, referrer_id: referrerId, reason, admin }) }),
};

/** What the plans pop-up can offer right now (backend billing.go). */
export interface BillingOptions {
  prices_usd_month: Record<string, number | null>;
  card: { available: boolean; provider: string | null };
  crypto: { available: boolean; networks: string[] };
  enforced: boolean;
}

export interface CryptoPaymentRequest {
  reference: string;
  network: string;
  address: string;
  amount_usdt: number;
  expires_at: string;
}

export const billingApi = {
  options: (): Promise<BillingOptions> => fetchApi('/api/v1/billing/options'),
  // The card provider's own payment page; the browser goes there.
  checkout: (tier: string): Promise<{ url: string }> =>
    fetchApi('/api/v1/billing/checkout', { method: 'POST', body: JSON.stringify({ tier }) }),
  crypto: (tier: string, network: string): Promise<CryptoPaymentRequest> =>
    fetchApi('/api/v1/billing/crypto', { method: 'POST', body: JSON.stringify({ tier, network }) }),
};

export const healthApi = {
  getHealth: (): Promise<HealthResponse> =>
    fetchApi('/healthz'),

  getReadiness: (): Promise<HealthResponse> =>
    fetchApi('/readyz'),

  getMetrics: (): Promise<MetricsResponse> =>
    fetchApi('/metrics'),

  getWebSocketHealth: (): Promise<{ status: string; client_count: number; timestamp: number }> =>
    fetchApi('/ws/health'),
};

/****************
 * Admin API methods (require authentication)
 ****************/

export const adminApi = {
  triggerPoll: (token: string): Promise<{ message: string; timestamp: number }> =>
    fetchApi('/api/v1/admin/trigger-poll', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    }),

  resetStatistic: (metric: string, token: string): Promise<{ message: string; timestamp: number }> =>
    fetchApi(`/api/v1/admin/reset/${metric}`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    }),
};

/****************
 * Dashboard API types
 ****************/

export interface DashboardSummaryResponse {
  total_volume: number;
  long_volume: number;
  short_volume: number;
  long_count: number;
  short_count: number;
  total_positions: number;
  active_traders: number;
  average_leverage: number;
  /** The closed trades' average leverage (average_leverage is the open book's). */
  closed_average_leverage?: number;
  top_assets: AssetVolumeData[];
  total_trades: number;
  win_count: number;
  loss_count: number;
  win_rate: number;
  total_realized_pnl: number;
  /** Every closed roster trade's realised PnL, total and by side (the rule
   *  the trader list sums), narrowed by the asset filter like the rest. */
  roster_pnl?: number;
  roster_long_pnl?: number;
  roster_short_pnl?: number;
  /** Listed traders with a closed trade in scope (of the chosen type). */
  listed_traders?: number;
  avg_trade_roe: number;
  avg_drawdown: number;
  avg_drawup: number;
  timestamp: number;
}

export interface AssetVolumeData {
  asset: string;
  volume: number;
  count: number;
  share: number;
  // Added 2026-05-16 (Fix #3): real per-side breakdown from backend.
  // Optional because older API responses (pre-fix) don't have these.
  long_count?: number;
  short_count?: number;
  long_volume?: number;
  short_volume?: number;
}

export interface TopTraderResponse {
  trader_uid: string;
  trader_name: string;
  trade_count: number;
  win_count: number;
  loss_count: number;
  win_rate: number;
  total_realized_pnl: number;
  avg_roe: number;
  active_positions: number;
  unrealized_pnl: number;
  // The worst single trade's FINAL result, sign-flipped. NOT a drawdown: it is
  // the outcome, where drawdown is the worst moment along the way. Kept for
  // older callers; max_drawdown_percent is the real measure. See rest/drawdown.go.
  min_roe: number;
  avg_duration_hours: number;
  // Full profile metric set — every one is available as a Top Traders column.
  sharpe_ratio?: number;
  sortino_ratio?: number;
  profit_factor?: number;
  max_drawdown_percent?: number;
  avg_drawdown_percent?: number;
  /** False when none of the trader's closed trades has a measured MAE: the two
   *  figures above are then 0 by default, not a measurement (2026-10-13). */
  max_drawdown_measured?: boolean;
  avg_margin?: number;
  /** Median and largest trade margin (10-21). */
  median_margin?: number;
  max_margin?: number;
  avg_leverage?: number;
  avg_win?: number;
  avg_loss?: number;
  wallet_size?: number;
  total_volume?: number;
  biggest_position?: number;
  net_position?: number;
  total_fees_usd?: number;
  // Direction split per trader. Optional because a response served before
  // 2026-09-03 has neither, and a trader with no closed trades has both at 0 --
  // the column must show nothing in both cases rather than "0% long", which
  // reads as "always short".
  long_count?: number;
  short_count?: number;
  // Only ever true when the caller asked for the below-floor traders.
  below_floor?: boolean;
  /** Fewer closed trades than the default list asks for (2026-10-15). */
  little_history?: boolean;
  /** Listed, but closed nothing in the chosen period: "-" for its figures (2026-10-16). */
  no_trades_in_period?: boolean;
  /** The profile's newer figures (2026-10-20); null = too little history. */
  worst_trade_percent?: number | null;
  best_mfe_percent?: number | null;
  avg_mfe_percent?: number | null;
  mfe_measured?: boolean;
  /** The Performance Metrics' last row (10-31, top_traders_excursion.go); null = too few trades. */
  point_of_no_return?: number | null;
  best_stop_loss?: number | null;
  avg_mfe_given_back?: number | null;
  profit_kept?: number | null;
  green_to_red?: number | null;
  best_take_profit?: number | null;
  cvar_95?: number | null;
  calmar?: number | null;
  equity_drawdown_percent?: number | null;
  profitable_months_percent?: number | null;
}

export interface EquityPoint {
  at: string;
  equity: number;
  realized_pnl: number;
  unrealized_pnl: number;
}

/****************
 * THE OPEN BOOK, AS THE BROKER HAS IT.
 *
 * One entry per position the exchange reports, with our sessions attached. The
 * shape mirrors the backend's open_positions.go; read that file for why the
 * venue's book is the list rather than our own rows.
 ****************/

/**
 * Trading on a venue key that CopyFi did not place.
 *
 * F-19. Counted only on symbols the key has never carried one of our orders on
 * — nothing of ours could have opened those, so there is no missing entry to
 * postulate. Deliberately an undercount: a symbol CopyFi has also traded is
 * excluded whole, because what this misses stays honestly "unaccounted" while
 * an overcount would tell a user they made trades they never made.
 *
 * `measured` false means the question could not be asked (no venue key, or a
 * failed read) — never render that as $0.00.
 */
export interface OutsideTrading {
  pnl: number;
  symbols: number;
  symbol_names?: string[];
  measured: boolean;
}

export interface OpenPositionSession {
  /**
   * What this session's leg of the position has cost.
   *
   * Per SESSION because that is the only level anyone can act at — the venue
   * holds one merged position and cannot tell two sessions on it apart, so if
   * these are not carried here nothing shows them.
   *
   * `fees_estimated` is true when any part of the total is still our own
   * arithmetic rather than the exchange's charge; funding is kept separate
   * because holding a position and trading it are different costs.
   */
  total_fees?: number;
  fees_estimated?: boolean;
  funding_paid?: number;
  order_count?: number;
  subscription_id: number;
  trader_name?: string;
  size: number;
  /** 0..1, this session's portion of what WE have attributed to the position. */
  share: number;
  /** The position's PnL times the share. Floating, and still at risk. */
  unrealized_pnl: number;
  /**
   * What this session has already BANKED on a position it still holds.
   *
   * G-21: an open position genuinely has two PnLs and a closed trade has one.
   * This is the second — it exists only after a partial close, it is not at
   * risk, and it does not disappear when the rest closes. Never add it to
   * `unrealized_pnl`: they answer different questions and a total answers
   * neither.
   *
   * Absent (not 0) on a position nobody has scaled out of, because a second
   * line reading $0.00 invents a distinction where there is none.
   */
  realised_so_far?: number;
}

export interface OpenPositionRow {
  /**
   * The venue's own margin figures, reported on every read.
   *
   * `margin_used` is what Phemex has actually reserved — better than anything
   * recomputed from entry and leverage, because it includes whatever has been
   * added since. `margin_mode` is the mode the CONTRACT is under, which on a
   * shared key is not always the one a strategy asked for. `roe` is the
   * unrealised PnL against that margin, never against notional.
   */
  margin_used: number;
  margin_mode?: string;
  roe: number;
  /** Where the venue takes the position away. 0 means it reported none —
   *  render nothing, never a liquidation at $0. */
  liquidation_price?: number;
  symbol: string;
  side: string;
  size: number;
  entry_price: number;
  mark_price: number;
  leverage: number;
  unrealized_pnl: number;
  /** Empty when the venue holds it and we cannot say which session opened it. */
  sessions: OpenPositionSession[];
  /** Two or more sessions on the same symbol and side — the case worth naming. */
  shared: boolean;
  /** What our rows sum to. Below `size` when attribution is incomplete. */
  attributed_size: number;
  /** Our row survives but the venue does not report the position. */
  venue_missing?: boolean;

  /** Sum of the sessions' sizes as replayed from their own filled orders, and
   *  whether it equals the broker's. The validation layer: SUM == broker. */
  basis_size?: number;
  basis_agrees?: boolean;
}

/** The user's own levels on an open position (api/position_controls.go). */
export interface PositionProtection { set: boolean; stop_loss?: number; take_profit?: number; updated_at?: string;
  /** What rests on the exchange now, read as the controls open; by_hand = not set from CopyFi. */
  venue?: { read: boolean; stop_loss?: number; stop_loss_by_hand?: boolean; take_profit?: number; take_profit_by_hand?: boolean } }
export interface ProtectionResult { practice: boolean; shared: boolean; level: number; order_id?: string; cancelled: number; put_back: boolean; bare: boolean; trailing: boolean }
export interface MarginResult { shared: boolean; margin: number; margin_known: boolean }

/** One settled trade, for the history under the open ones. */
export interface RecentlyClosedTrade {
  trade_id: string;
  subscription_id: number;
  trader_name?: string;
  symbol: string;
  direction: string;
  leverage: number;
  size: number;
  entry_price: number;
  exit_price: number;
  /** Gross: the venue's result before costs. The tray shows it net of
   *  total_fees and funding_paid, the basis every total is on (2026-09-10). */
  realized_pnl: number;
  roe: number;
  total_fees: number;
  funding_paid: number;
  exit_time?: string;
  duration_hours: number;
}

export interface WaitingOrder {
  order_id: number;
  subscription_id: number;
  symbol: string;
  direction: string;
  action: string;
  size: number;
  price: number;
  placed_at: string;
  waiting_hours: number;
}

export interface OpenPositionsResponse {
  positions: OpenPositionRow[];
  count: number;
  /**
   * The last few settled trades across every session, newest first.
   *
   * On this payload rather than its own request so both halves come from one
   * instant: with two requests, a trade closing between them would show up in
   * neither list or in both. Already ordered by the server — re-sorting or
   * slicing differently here would undo that guarantee.
   */
  recently_closed?: RecentlyClosedTrade[];
  /**
   * Orders the broker is HOLDING that have not filled.
   *
   * Guillaume, 2026-09-16: "While the Limit Order was waiting to be filled, in
   * the Open Trades Trail, it was not display." It could not be — this tray
   * renders positions, and an order that has not filled has no position. He
   * found out his was there by looking at Phemex, and cancelled it by hand.
   */
  waiting_at_the_broker?: WaitingOrder[];
  /** The account total, from the venue's numbers — the same source the
   *  dashboard's Open PnL reads, so the two cannot disagree. */
  unrealized_pnl: number;
}

export interface EquityHistoryResponse {
  points: EquityPoint[];
  current: number;
  /** False until two snapshots exist. There is no change from one reading. */
  change_available: boolean;
  change_abs: number;
  change_pct: number;
  /**
   * Wallet that arrived inside the window rather than being earned: a session
   * started, or one whose declared wallet was raised. `change_abs` EXCLUDES it
   * -- opening a $10,000 paper session is not a $10,000 gain -- and this is
   * here so the UI can say so instead of leaving the two numbers to disagree
   * without explanation.
   */
  capital_added: number;
  /**
   * Trading on this key that CopyFi did not place, given its own line so it
   * stops landing in the gap between the wallet and Net PnL. Absent for a scope
   * with no venue key behind it, and absent when the read failed — see
   * `OutsideTrading.measured`.
   */
  outside_trading?: OutsideTrading;
  /**
   * Positions the BROKER holds, from the latest snapshot. Our own position rows
   * can be empty while the account holds several — on a shared key the merged
   * position is not attributable to a session, so nothing is stored — and
   * printing 0 there is a false statement rather than a limitation. 2026-09-06.
   */
  open_positions: number;
  /**
   * When the open half of `current` was last read from the exchange. Absent
   * when the figure is the hourly snapshot alone, so a reader can tell a
   * number that is seconds old from one that may be an hour old.
   */
  live_as_of?: string;
  /** When the comparison point actually is — may be younger than the window. */
  oldest_at?: string;
  window_hours: number;
  scope: string;
}

export interface RecentTradeResponse {
  // UUID trade_id from the `trades` table (string). Used as React key + modal
  // arg only — never numeric math. GetTradeOrders accepts string ids.
  id: string;
  trader_uid: string;
  trader_name: string;
  symbol: string;
  side: string;
  closing_pnl: number;
  status: string;
  leverage: number;
  roe: number;
  entry_price: number;
  close_price: number;
  max_amount: number;
  opened_at: string | null;
  closed_at: string | null;
  order_count?: number;
  order_ids?: number[];
  // Added 2026-05-16 (Recent Trades widget expansion):
  margin_dollar?: number;
  drawdown_pct?: number;
  total_fees?: number;
  increase_count?: number;
  decrease_count?: number;
}

export interface AssetStatsResponse {
  symbol: string;
  trade_count: number;
  total_pnl: number;
  win_count: number;
  loss_count: number;
  win_rate: number;
  avg_roe: number;
}

export interface ActivePositionResponse {
  id: number;
  trader_uid: string;
  trader_name: string;
  asset: string;
  remain_amount: number;
  entry_price: number;
  mark_price: number;
  unrealized_pnl: number;
  unrealized_pnl_pct: number;
  leverage: number;
  side: string;
  is_futures: boolean;
  /** The position's own open time; null when it is not a fact (server, 2026-10-08). */
  opened_at?: string | null;
  opened_at_source?: string;
  created_at: string;
  updated_at: string;
}

/****************
 * Dashboard API methods - REAL DATA FROM DATABASE
 ****************/

export const dashboardApi = {
  /**
   * The stats bar above the Traders page. Venue-scoped since 2026-09-12: without it the
   * tiles described every venue at once and never moved when the selector did, while the
   * trader count and PnL beside them came from the venue-scoped roster.
   */
  getSummary: (period?: string, venue?: string, assets?: AssetFilter, includeHidden = false, set?: number | null): Promise<DashboardSummaryResponse> => {
    const params = new URLSearchParams();
    if (period && period !== 'all') params.set('period', period);
    if (venue) params.set('venue', venue);
    // The page's "include heavy losers and little history" (2026-10-19): the
    // tiles count the traders the list shows, so they follow it too.
    if (includeHidden) params.set('include_below_floor', 'true');
    // A group's lens (trader_sets.go): only its members.
    if (set != null) params.set('set', String(set));
    const qs = (params.toString() + assetFilterQuery(assets)).replace(/^&/, '');
    return fetchApi(`/api/v1/dashboard/summary${qs ? `?${qs}` : ''}`);
  },

  /* No default limit. It was 100, which meant a caller passing `undefined` to
     mean "the whole open book" got 100 rows -- about 3% of it -- and no error
     saying so. The handler's own default is RosterListUnlimited, so omitting
     the parameter is how you ask for everything; a number still pages.
     2026-09-23. */
  getActivePositions: (limit?: number, venue?: string): Promise<{ positions: ActivePositionResponse[]; count: number; total: number; timestamp: number }> => {
    const qs = new URLSearchParams();
    if (limit != null) qs.set('limit', String(limit));
    if (venue) qs.set('venue', venue);
    const q = qs.toString();
    return fetchApi(`/api/v1/dashboard/positions${q ? `?${q}` : ''}`);
  },

  // includeBelowFloor is the deliberate opt-in that lifts the catastrophic-loss
  // floor. It is a required argument, not an optional one: a caller that forgets
  // it would quietly publish the traders every default list exists to keep out,
  // and that failure is invisible until someone copies one.
  /*
   * venue: omit → the backend defaults to 'binance'. This roster had NO venue
   * filter at all until 2026-09-09, so MEXC traders were on every user's
   * dashboard; the default is the intended behaviour, not the previous one.
   */
  /* limit 0 asks for every trader on the roster: the endpoint ignores a limit
     that is not above 0 and applies none (RosterListUnlimited). 2026-10-07. */
  getTopTraders: (limit: number = 50, period?: string, includeBelowFloor: boolean = false, venue?: string, assets?: AssetFilter): Promise<{ traders: TopTraderResponse[]; count: number; timestamp: number }> =>
    fetchApi(`/api/v1/dashboard/traders?limit=${limit > 0 ? limit : 0}${period && period !== 'all' ? `&period=${period}` : ''}${includeBelowFloor ? '&include_below_floor=true' : ''}${venue ? `&venue=${venue}` : ''}${assetFilterQuery(assets)}`),

  /*
   * venue: omit → the backend defaults to 'binance'.
   *
   * Both feeds are the same query with two orderings, and until 2026-09-09
   * neither had a venue at ANY layer — the parameter did not exist on the Go
   * function either, so no caller could have asked. An admin saw MEXC traders
   * on every roster and none of their trades in these two panels.
   */
  /* The three feeds page: limit is the page size, offset where it starts, and
     total the count of the whole feed. 2026-10-07. */
  getRecentTrades: (limit: number = 50, period?: string, venue?: string, assets?: AssetFilter, offset: number = 0): Promise<{ trades: RecentTradeResponse[]; count: number; total?: number; timestamp: number }> =>
    fetchApi(`/api/v1/dashboard/trades/recent?limit=${limit}${offset > 0 ? `&offset=${offset}` : ''}${period && period !== 'all' ? `&period=${period}` : ''}${venue ? `&venue=${venue}` : ''}${assetFilterQuery(assets)}`),

  /** Positions the traders opened most recently and still hold (Dashboard, third view). */
  getRecentlyOpenedTrades: (limit: number = 50, venue?: string, assets?: AssetFilter, offset: number = 0): Promise<{ trades: RecentTradeResponse[]; count: number; total?: number; timestamp: number }> =>
    fetchApi(`/api/v1/dashboard/trades/opened?limit=${limit}${offset > 0 ? `&offset=${offset}` : ''}${venue ? `&venue=${venue}` : ''}${assetFilterQuery(assets)}`),

  getTopTrades: (limit: number = 20, period?: string, venue?: string, assets?: AssetFilter, offset: number = 0): Promise<{ trades: RecentTradeResponse[]; count: number; total?: number; timestamp: number }> =>
    fetchApi(`/api/v1/dashboard/trades/top?limit=${limit}${offset > 0 ? `&offset=${offset}` : ''}${period && period !== 'all' ? `&period=${period}` : ''}${venue ? `&venue=${venue}` : ''}${assetFilterQuery(assets)}`),

  /** Section two's whole band, served from the backend's own refreshed cache. */
  getMarketOverview: (): Promise<any> => fetchApi(`/api/v1/market/overview`),

  /** The regime tensor's live reading (regime_read.go): Easy-Hard, Volatility, Regime. */
  getRegimeLive: (): Promise<RegimeLiveResponse> => fetchApi(`/api/v1/regime/live`),

  /** Large aggressive trades, collected from the same exchange sockets. */
  getWhaleActivity: (range: '1h' | '4h' | '24h' | '7d' = '24h'): Promise<any> =>
    fetchApi(`/api/v1/market/whales?range=${range}`),

  /** Liquidations we collect ourselves from the exchange sockets. */
  getLiquidations: (range: '1h' | '4h' | '24h' | '7d' = '24h'): Promise<any> =>
    fetchApi(`/api/v1/market/liquidations?range=${range}`),

  getAssetStats: (period?: string): Promise<{ assets: AssetStatsResponse[]; count: number; timestamp: number }> =>
    fetchApi(`/api/v1/dashboard/assets${period && period !== 'all' ? `?period=${period}` : ''}`),
};

/****************
 * Trader API - per-trader data for Profile page
 ****************/

export interface TraderListItem {
  id: string;
  trader_name: string;
  trade_count: number;
  /* The long/short split. Returned by /api/v1/traders since the list was built
     and never declared here, so the direction-filter warning read two fields
     TypeScript said did not exist — it worked, because the data is there, but
     nothing would have caught a rename. Names and types checked against the
     live response, not inferred from the Go struct. */
  long_trade_count: number;
  short_trade_count: number;
  total_pnl: number;
  // total_pnl split by side, over the same closed trades (2026-09-26).
  long_pnl?: number;
  short_pnl?: number;
  wallet_size: number;
  is_active: boolean;
  last_order_time?: string;
  /** Shown only behind the include-hidden switch, and labelled (2026-10-15). */
  below_floor?: boolean;
  little_history?: boolean;
}

export interface TraderPerformanceResponse {
  trader_id: string;
  trader_name: string;
  total_trades: number;
  win_count: number;
  loss_count: number;
  win_rate: number;
  total_pnl_usd: number;
  roi_percent: number;
  avg_leverage: number;
  active_positions: number;
  unrealized_pnl: number;
  sharpe_ratio: number;
  sortino_ratio: number;
  profit_factor: number;
  max_drawdown_percent: number;
  avg_trade_duration: number;
  margin_balance: number;
  timestamp: number;
  /** Hyperliquid traders only: the full wallet the short "hl:" id stands for. */
  wallet_address?: string;
}

export interface RestingOrder {
  credential_id: number;
  symbol: string;
  side: string;
  pos_side: string;
  order_type: string;
  status: string;
  price: number;
  trigger_price: number;
  quantity: number;
  reduce_only: boolean;
  /** What the order does: stop_loss, take_profit, protective, or entry. */
  kind: 'stop_loss' | 'take_profit' | 'protective' | 'entry';
  /** 'copyfi' when we placed it, 'you' when it came from anywhere else. */
  placed_by: 'copyfi' | 'you';
  order_id: string;
  /** OUR replicated_orders id, set only for orders CopyFi placed and matched
   *  back to a session. Absent on anything the user set at the exchange, which
   *  is what keeps the Cancel button off those rows. M-97, 2026-09-23. */
  our_order_id?: number;
  /** A stop or target the broker booked from one our strategy attached to an
   *  entry, matched by price. It has no our_order_id; its Cancel goes through
   *  cancelRestingOrder, which re-checks it against the live book. */
  strategy_stop?: boolean;
  cl_ord_id: string;
  subscription_id: number;
}

export interface RestingOrdersResponse {
  orders: RestingOrder[];
  symbols_unanswered: string[];
  refusals: string[];
  fetched_at: string;
}

export interface TraderTradeResponse {
  id: number;
  trader_id: string;
  symbol: string;
  /** Open, no exact time: the position was open by this date (review #90). */
  entry_time_before?: string | null;
  direction: string;
  status: string;
  avg_entry_price: number;
  /** True when copying started mid-position: part of the entry was never
   *  observed and the price is back-filled from the exit. The UI marks it. */
  is_partial?: boolean;
  avg_exit_price?: number;
  initial_size?: number;
  current_size: number;
  leverage: number;
  pnl_usd: number;
  pnl_percent: number;
  entry_time: string | null;
  exit_time?: string | null;
  order_count?: number;
  order_ids?: number[];
  /** Server-computed notional/leverage. Present on both open and closed. */
  margin_dollar?: number;
  /** Sum of the trade's estimated fees. Closed trades only: an open position
   *  is read from the venue's position feed, which has no orders behind it. */
  total_fees?: number;
  /** Funding over the trade, apart from the fee (F-7). null = not priced yet. */
  funding_fee?: number | null;
  /** The venue's own mark at the last scrape. Open positions only. Used when
   *  the live price stream has no tick for the symbol, which is why the Mark
   *  column was blank on many MEXC and Binance rows. */
  mark_price?: number;
}

export const traderApi = {
  getAllTraders: (search?: string, period?: string, venue?: string, includeHidden = false): Promise<TraderListItem[]> => {
    const params = new URLSearchParams();
    // The Dashboard's opt-in (2026-10-15): heavy losers and little history.
    if (includeHidden) params.set('include_below_floor', 'true');
    if (search) params.set('search', search);
    if (period && period !== 'all') params.set('period', period);
    // venue: omit → backend defaults to 'binance' (unchanged for normal users).
    // Admin-only UI passes 'mexc' or 'all' to surface the second venue. 2026-08-30.
    if (venue && venue !== 'binance') params.set('venue', venue);
    const qs = params.toString();
    return fetchApi(`/api/v1/traders${qs ? `?${qs}` : ''}`);
  },

  /* A trader's name back to the trader, for a name that is not on the loaded
     roster (2026-10-01: a trader's address is his name). `matches` is empty
     for a name nobody has and holds two when two traders share it. */
  resolveName: (name: string, venue?: string): Promise<{ name: string; matches: Array<{ id: string; trader_name: string }> }> =>
    fetchApi(`/api/v1/traders/resolve?name=${encodeURIComponent(name)}${venue ? `&venue=${encodeURIComponent(venue)}` : ''}`),

  getTraderPerformance: (traderId: string, period?: string): Promise<TraderPerformanceResponse> =>
    fetchApi(`/api/v1/traders/performance?trader_id=${encodeURIComponent(traderId)}${period && period !== 'all' ? `&period=${encodeURIComponent(period)}` : ''}`),

  getTraderTrades: (traderId: string, status?: 'OPEN' | 'CLOSED', limit: number = 100, period?: string, lean?: boolean): Promise<TraderTradeResponse[]> =>
    fetchApi(`/api/v1/traders/trades?trader_id=${encodeURIComponent(traderId)}${status ? `&status=${status}` : ''}&limit=${limit}${period && period !== 'all' ? `&period=${encodeURIComponent(period)}` : ''}${lean ? '&lean=1' : ''}`),

  /* Paged. `getTraderTrades` above is left exactly as it was -- it is called
     from several places that only ever want the first page, and changing its
     return shape would touch all of them for no benefit.

     total is the count BEFORE the limit, so a profile can finally say "1 of
     16,000" instead of showing 5,000 and looking complete. nextCursor is
     absent once a page does not fill, which is how the caller knows to stop.
     2026-09-08. */
  getTraderTradesPage: async (
    traderId: string,
    opts?: { status?: 'OPEN' | 'CLOSED'; limit?: number; period?: string; cursor?: string },
  ): Promise<{ trades: TraderTradeResponse[]; total: number | null; nextCursor: string | null }> => {
    const p = new URLSearchParams({ trader_id: traderId });
    if (opts?.status) p.set('status', opts.status);
    p.set('limit', String(opts?.limit ?? 500));
    if (opts?.period && opts.period !== 'all') p.set('period', opts.period);
    if (opts?.cursor) p.set('cursor', opts.cursor);

    let total: number | null = null;
    let nextCursor: string | null = null;
    const trades = await fetchApi<TraderTradeResponse[]>(`/api/v1/traders/trades?${p.toString()}`, {
      onResponse: (r) => {
        const t = r.headers.get('X-Total-Count');
        /* Number('') is 0, which would read as "this trader has no trades".
           Absent must stay null so the caller can fall back to the row count. */
        if (t !== null && t !== '') {
          const n = Number(t);
          if (Number.isFinite(n)) total = n;
        }
        nextCursor = r.headers.get('X-Next-Cursor');
      },
    });
    return { trades, total, nextCursor };
  },

  getTradeOrders: (tradeId: string | number): Promise<{ orders: InferredOrderResponse[]; count: number; trade_id: string; timestamp: number }> =>
    fetchApi(`/api/v1/trades/${tradeId}/orders`),

  getWalletHistory: (
    traderId: string,
  ): Promise<{
    mode: 'recorded' | 'reconstructed' | 'hybrid';
    exact: boolean;
    count: number;
    points: { t: string; balance: number }[];
    /* Where the reconstruction stops being impossible. Null when the whole
       curve is possible, which is the common case. A negative balance is the
       back-track failing rather than a measurement, so the chart starts here
       and says what it is not drawing. */
    reliable_from: string | null;
    negative_points: number;
    worst_balance: number;
  }> =>
    fetchApi(`/api/v1/traders/${encodeURIComponent(traderId)}/wallet-history`),

  // Deposit/withdrawal history for the wallet curve (nightly-synced
  // trader_transfers table — see trader_transfers.go). amount is SIGNED:
  // positive is money in. 2026-08-29 (wallet-curve module wiring).
  getTraderTransfers: (traderId: string): Promise<{ transfers: { transferred_at: string; coin: string; amount: number; direction: 'DEPOSIT' | 'WITHDRAWAL' }[]; count: number }> =>
    fetchApi(`/api/v1/traders/${encodeURIComponent(traderId)}/transfers`),
};

/****************
 * Favorites API — per-user starred traders
 ****************/

/**
 * Traders this user has decided not to copy again — the opposite of a
 * favourite, and a stronger statement.
 *
 * Deliberately the same three calls in the same shape as `favoritesApi`: they
 * are two answers to one question, and a reader comparing them should find
 * nothing to compare.
 */
/****************
 * GROUPS AND CATEGORIES OF TRADERS, phase 1 (backend rest/trader_sets.go,
 * 2026-10-26). Admin paths: admins only until Guillaume releases them.
 ****************/
export interface TraderSetCriteria {
  venue?: string;
  include_below_floor?: boolean;
  asset_class?: string;
  broker?: string;
  filters?: Record<string, { min?: string; max?: string }>;
}
export interface TraderSet {
  id: number;
  kind: 'group' | 'category';
  name: string;
  description?: string;
  criteria?: TraderSetCriteria;
  member_count: number;
  members?: string[];
  refreshed_at?: string;
  refresh_error?: string;
  created_at: string;
  updated_at: string;
}
export const traderSetsApi = {
  list: (): Promise<{ sets: TraderSet[] }> => fetchApi('/api/v1/trader-sets'),
  get: (id: number): Promise<TraderSet> => fetchApi(`/api/v1/trader-sets/${id}`),
  create: (body: { kind: 'group' | 'category'; name: string; description?: string; members?: string[]; criteria?: TraderSetCriteria }): Promise<TraderSet> =>
    fetchApi('/api/v1/trader-sets', { method: 'POST', body: JSON.stringify(body) }),
  update: (id: number, body: { name?: string; description?: string; members?: string[]; criteria?: TraderSetCriteria }): Promise<TraderSet> =>
    fetchApi(`/api/v1/trader-sets/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  remove: (id: number): Promise<{ deleted: boolean }> => fetchApi(`/api/v1/trader-sets/${id}`, { method: 'DELETE' }),
  members: (id: number, change: { add?: string[]; remove?: string[] }): Promise<TraderSet> =>
    fetchApi(`/api/v1/trader-sets/${id}/members`, { method: 'POST', body: JSON.stringify(change) }),
  preview: (criteria: TraderSetCriteria): Promise<{ members: string[]; member_count: number }> =>
    fetchApi('/api/v1/trader-sets/preview', { method: 'POST', body: JSON.stringify(criteria) }),
};

/****************
 * COPYING A GROUP OR CATEGORY AS ONE SESSION (backend replicator/api/
 * group_sessions.go, phase 3). Each member is an ordinary session; the group
 * holds no money of its own: its figures are its members' sum.
 ****************/
export interface GroupSessionMember {
  id: number;
  group_id: number;
  subscription_id: number;
  trader_uid: string;
  joined_at: string;
  left_at?: string;
  status: string;
  total_pnl: number;
}
export interface GroupSession {
  id: number;
  name: string;
  trader_set_id: number;
  set_kind: 'group' | 'category';
  credential_id: number;
  delivery_mode: string;
  margin_type: 'crossed' | 'isolated';
  starting_balance: number;
  kill_pnl?: number;
  kill_drawdown_pct?: number;
  stop_detail?: string;
  status: 'active' | 'paused' | 'stopped';
  created_at: string;
  members: GroupSessionMember[];
  total_pnl: number;
}
export const groupSessionsApi = {
  list: (): Promise<{ groups: GroupSession[] }> => fetchApi('/api/v1/group-sessions'),
  create: (body: {
    name: string;
    trader_set_id: number;
    credential_id: number;
    delivery_mode: 'broker' | 'paper';
    starting_balance: number;
    margin_type: 'crossed' | 'isolated';
    default_config: any;
    members?: { trader_uid: string; model_config?: any; margin_cap_pct?: number; strategy_name?: string }[];
    kill_pnl?: number;
    kill_drawdown_pct?: number;
    /** A competition session: the server sets paper and the competition wallet. */
    competition?: boolean;
    /** The user's AI for every member (round 11 #512): the single session's fields. */
    use_ai?: boolean;
    ai_decider?: string;
    ai_terms_version?: string;
    ai_context_spec?: string;
    ai_fallback?: string;
  }): Promise<{ session_id: number; traders: string[]; warnings?: string[] }> =>
    fetchApi('/api/v1/group-sessions', { method: 'POST', body: JSON.stringify(body) }),
  /** A group session's traders, each paused or stopped on its own (round 11). */
  traders: (sessionId: number): Promise<{ traders: { trader_uid: string; strategy_name?: string; status: 'active' | 'paused' | 'stopped'; stopped_at?: string }[] }> =>
    fetchApi(`/api/v1/replicator/subscriptions/${sessionId}/traders`),
  setTrader: (sessionId: number, traderUid: string, status: 'active' | 'paused' | 'stopped'): Promise<{ status: string; positions_closed: number }> =>
    fetchApi(`/api/v1/replicator/subscriptions/${sessionId}/traders/${encodeURIComponent(traderUid)}`, { method: 'PUT', body: JSON.stringify({ status }) }),
  setStatus: (id: number, status: 'active' | 'paused' | 'stopped', pauseMode?: string): Promise<{ failed: number; members: { trader_uid: string; error?: string }[] }> =>
    fetchApi(`/api/v1/group-sessions/${id}`, { method: 'PUT', body: JSON.stringify({ status, pause_mode: pauseMode }) }),
};

/** A group or category backtest (backend backtest/runner_group.go): one shared replay, one
 *  ordinary backtest per member (open one with /copy-trade?tab=backtest&backtest=<id>). */
export interface GroupBacktest {
  id: number;
  title: string;
  trader_set_id: number;
  set_kind: 'group' | 'category';
  period_start: string;
  period_end: string;
  starting_balance: number;
  status: 'queued' | 'running' | 'done' | 'failed';
  progress: number;
  error?: string;
  stopped?: string;
  stopped_why?: string;
  summary?: { net_pnl: number; final_equity: number; return_pct: number; max_drawdown_pct: number; trades: number; members: number };
  members: { backtest_id: number; trader_uid: string }[];
  created_at: string;
  /** The group's own account, minute by minute (GET one only). */
  curve?: { t: string; realised: number; equity: number }[];
}
export const groupBacktestsApi = {
  list: (): Promise<{ groups: GroupBacktest[] }> => fetchApi('/api/v1/group-backtests'),
  /** One, with its curve: the group's own result (10-31). */
  get: (id: number): Promise<GroupBacktest> => fetchApi(`/api/v1/group-backtests/${id}`),
  create: (body: {
    title: string; trader_set_id: number; from: string; to: string; starting_balance: number;
    members: { trader_uid: string; trader_name?: string; strategy_name: string; strategy: any }[];
    broker_filter?: string;
    kill_pnl?: number; kill_drawdown_pct?: number;
    /** Cross or isolated for every member (round 11 #515). */
    margin_type?: 'crossed' | 'isolated';
    /** The user's AI for every member (round 11 #512): re-asks their model; ai_max_calls in total. */
    ai_mode?: 'reask'; ai_context_spec?: string; ai_fallback?: string; ai_max_calls?: number;
  }): Promise<GroupBacktest> => fetchApi('/api/v1/group-backtests', { method: 'POST', body: JSON.stringify(body) }),
  /** Delete one and its members' backtests (10-31); refused while it runs. */
  remove: (id: number): Promise<{ deleted: boolean }> => fetchApi(`/api/v1/group-backtests/${id}`, { method: 'DELETE' }),
};

/** A GET that carries who is asking: a lensed request (?set=) must, the set being the caller's own. */
export const fetchSignedIn = <T,>(endpoint: string): Promise<T> => fetchApi<T>(endpoint);

export const bannedTradersApi = {
  list: (): Promise<string[]> =>
    fetchApi('/api/v1/banned-traders'),
  add: (traderUid: string): Promise<void> =>
    fetchApi('/api/v1/banned-traders', { method: 'POST', body: JSON.stringify({ trader_uid: traderUid }) }),
  remove: (traderUid: string): Promise<void> =>
    fetchApi(`/api/v1/banned-traders?trader_uid=${encodeURIComponent(traderUid)}`, { method: 'DELETE' }),
};

/** What the speed run's cleanup calls when the admin leaves it (lib/speedRunLens.core.ts cleanUpRun). */
export const speedRunApi = {
  get: (endpoint: string): Promise<any> => fetchApi(endpoint),
  del: (endpoint: string): Promise<any> => fetchApi(endpoint, { method: 'DELETE' }),
};

export const favoritesApi = {
  list: (): Promise<string[]> =>
    fetchApi('/api/v1/favorites'),
  add: (traderUid: string): Promise<void> => {
    if (inSpeedRun()) rememberRunFavorite(traderUid);
    return fetchApi('/api/v1/favorites', { method: 'POST', body: JSON.stringify({ trader_uid: traderUid }) });
  },
  remove: (traderUid: string): Promise<void> =>
    fetchApi(`/api/v1/favorites?trader_uid=${encodeURIComponent(traderUid)}`, { method: 'DELETE' }),
};

/****************
 * Inferred Orders API - order-level events from position delta tracking
 ****************/

export interface InferredOrderResponse {
  id: number;
  trade_id?: string;
  position_history_id?: number;
  trader_uid: string;
  symbol: string;
  action_type: string;  // OPEN, INCREASE, DECREASE, CLOSE
  direction: string;    // LONG or SHORT
  execution_price: number;
  size_delta: number;
  size_after: number;
  leverage: number;
  nominal_value: number;
  margin_delta: number;
  estimated_fee: number;
  inferred_order_type: string;  // MARKET, LIMIT, STOP_LOSS, TAKE_PROFIT, LIQUIDATION
  order_type_confidence: number;
  realized_pnl_delta: number;
  /** The ranked figure the trade is built from: shared-close share, then the
   *  exchange's own number, then ours. Optional so an older payload still parses. */
  realised_counted?: number;
  unrealized_pnl_after: number;
  inferred_at: string;
  estimated_execution_time?: string;
  created_at: string;
}

export const ordersApi = {
  getOrders: (params?: {
    traderId?: string;
    symbol?: string;
    actionType?: string;
    limit?: number;
    period?: string;   // Added 2026-05-16: matches Open/Closed trades windowing
    /* next_cursor from the previous page (trader_id only). 2026-10-07. */
    cursor?: string;
  }): Promise<{ orders: InferredOrderResponse[]; count: number; timestamp: number;
    /* With trader_id: every order under these filters, before paging, and the
       cursor of the next page ('' on the last one). */
    total?: number; next_cursor?: string }> => {
    const searchParams = new URLSearchParams();
    if (params?.traderId) searchParams.set('trader_id', params.traderId);
    if (params?.symbol) searchParams.set('symbol', params.symbol);
    if (params?.actionType) searchParams.set('action_type', params.actionType);
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    if (params?.period && params.period !== 'all') searchParams.set('period', params.period);
    if (params?.cursor) searchParams.set('cursor', params.cursor);
    const query = searchParams.toString();
    return fetchApi(`/api/v1/orders${query ? `?${query}` : ''}`);
  },
};

/****************
 * Learning Center API - educational video CRUD
 ****************/

export interface LearningVideoResponse {
  id: number;
  title: string;
  description: string | null;
  youtube_url: string;
  category: string;
  created_at: string;
}

export const learningApi = {
  getVideos: (): Promise<LearningVideoResponse[]> =>
    fetchApi('/api/v1/learning-videos'),

  addVideo: (video: { title: string; description?: string; youtube_url: string; category: string }): Promise<LearningVideoResponse> =>
    fetchApi('/api/v1/learning-videos', {
      method: 'POST',
      body: JSON.stringify(video),
    }),

  deleteVideo: async (id: number | string): Promise<void> => {
    const response = await fetch(`${API_BASE}/api/v1/learning-videos/${id}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      throw new ApiError(await response.text().catch(() => 'Unknown error'), response.status);
    }
  },

  reorderVideos: (ids: (number | string)[]): Promise<void> =>
    fetchApi('/api/v1/learning-videos/reorder', {
      method: 'POST',
      body: JSON.stringify({ ids: ids.map(Number) }),
    }),
};

/****************
 * Subscription / Replicator API methods
 ****************/

/****************
 * The Orb's assistant.
 *
 * The model host and its key live in the backend, never here: anything in a
 * VITE_* variable is inlined into this bundle and readable by anyone who opens
 * devtools. The client sends a question and gets an answer, and nothing about
 * where the model runs is knowable from the shipped JS.
 ****************/
export const assistantApi = {
  /** Whether a model is wired at all, so the UI can avoid offering a chat box
   *  that would always fail. */
  status: (): Promise<{ available: boolean; model: string }> =>
    fetchApi('/api/v1/assistant/status'),

  /** `guide` is present when the Orb wants to offer to SHOW the thing it just
   *  described: a validated id from orbGuides.ts, never the model's raw word —
   *  the backend drops anything not on its own list. Absent on most answers. */
  ask: (
    message: string,
    history: { role: 'user' | 'assistant'; content: string }[],
    context?: string,
  ): Promise<{ answer: string; model: string; guide?: string; fill?: import('@/lib/orbFill.core').FillChange[]; checkin_due?: boolean; ref?: string }> =>
    fetchApi('/api/v1/assistant/chat', {
      method: 'POST',
      body: JSON.stringify({ message, history, context }),
    }),

  /** The person's thumbs on one answer (assistant_learning.go, 2026-10-22). */
  rate: (ref: string, rating: 1 | -1): Promise<{ ok: boolean }> =>
    fetchApi('/api/v1/assistant/rate', { method: 'POST', body: JSON.stringify({ ref, rating }) }),
  /** Still stuck after a thumbs down: a person (assistant_learning.go, 10-31). */
  stillStuck: (ref: string): Promise<{ ok: boolean }> =>
    fetchApi('/api/v1/assistant/still-stuck', { method: 'POST', body: JSON.stringify({ ref }) }),

  /** Something the Orb wants to raise unasked (a daily check found the user's
   *  orders failing for a reason they can fix). null when there is nothing. */
  // lang: the app's language. The server translates the Orb's message into it
  // once and keeps it (assistant_nudge_translate.go), and remembers it for the
  // follow-up email (orb_nudge_email_lang.go). Sent for English too.
  nudge: (lang?: string): Promise<{ nudge: OrbNudge | null }> =>
    fetchApi('/api/v1/assistant/nudge' + (lang ? `?lang=${encodeURIComponent(lang)}` : '')),

  /** The nudge was shown: the Orb stops calling for attention. */
  nudgeSeen: (id: number): Promise<{ ok: boolean }> =>
    fetchApi('/api/v1/assistant/nudge/seen', { method: 'POST', body: JSON.stringify({ id }) }),

  /** The person's profile from the Orb's first conversation, and whether the
   *  app should open that conversation now (backend assistant_profile.go). */
  profile: (): Promise<{
    profile: OrbProfile | null; offer_interview: boolean; type_name?: string;
    /** A check-in is due (assistant_profile_checkin.go): time, the person, or what they do changed. */
    offer_checkin?: boolean; checkin_trigger?: string;
  }> =>
    fetchApi('/api/v1/assistant/profile'),

  /** 'request': "Update my profile" (due at once); 'later': not now (14 days). */
  checkin: (action: 'request' | 'later'): Promise<{ offer_checkin: boolean }> =>
    fetchApi('/api/v1/assistant/profile/checkin', { method: 'POST', body: JSON.stringify({ action }) }),

  /** One answer in the first conversation; action 'skip' = not now. */
  interview: (
    message: string,
    history: { role: 'user' | 'assistant'; content: string }[],
    context?: string,
    action?: 'skip' | 'restart',
  ): Promise<{ answer?: string; done?: boolean; model?: string; check_in?: boolean; route?: 'chat' }> =>
    fetchApi('/api/v1/assistant/interview', {
      method: 'POST',
      body: JSON.stringify({ message, history, context, action }),
    }),

  /** The conversation as the server has it, the same on every device: the
   *  recent exchanges since the last clear (backend assistant_conversation.go). */
  conversation: (): Promise<{ turns: { role: 'user' | 'assistant'; content: string }[] }> =>
    fetchApi('/api/v1/assistant/conversation'),

  /** Clear: later reads start empty; the server's own log is kept. */
  clearConversation: (): Promise<{ ok: boolean }> =>
    fetchApi('/api/v1/assistant/conversation', { method: 'DELETE' }),

  /** How the person wants the Orb's one notice (backend assistant_notice.go):
   *  ask = not answered yet and a live session running; session_id is the
   *  session to request a Telegram link code for. */
  notice: (): Promise<OrbNoticeState> => fetchApi('/api/v1/assistant/notice'),

  setNotice: (channel: OrbNoticeChannel): Promise<OrbNoticeState> =>
    fetchApi('/api/v1/assistant/notice', { method: 'POST', body: JSON.stringify({ channel }) }),
};

export type OrbNoticeChannel = 'email' | 'telegram' | 'none';

export interface OrbNoticeState {
  channel: OrbNoticeChannel | '';
  ask: boolean;
  session_id?: number;
  telegram_linked: boolean;
}

/** What the Orb learnt in its first conversation (assistant_profile.go). */
export interface OrbProfile {
  status: 'in_progress' | 'done' | 'skipped';
  answers: number;
  type?: string;
  fields: Record<string, unknown>;
}

/** One thing the Orb raises on its own. The message is written by the backend,
 *  word for word, so what arrives unasked is always exactly right. */
export interface OrbNudge {
  id: number;
  /** A live message's trigger (orbLive.ts): what its "Did this help?" is about (10-25). */
  trigger?: string;
  /** In practice always 'DIAGNOSIS' since 2026-09-22 — the five older kinds
   *  each saw one cause and the first to fire won. The union is kept because
   *  rows written under the old kinds may still be resolved. */
  kind: 'DIAGNOSIS' | 'TOO_SMALL' | 'NO_FUNDS' | 'NO_FUNDS_DNA' | 'DEAD_KEY' | string;
  message: string;
  created_at: string;
  /** A spotlight sequence that answers this nudge, derived server-side from
   *  the diagnosis codes and validated against the same closed list the chat
   *  uses. Absent for most codes, deliberately — a nudge whose remedy is
   *  "change rules you cannot change while the session runs" gets no button. */
  guide?: string;
}

/**
 * What happened to one session under Pause All / Stop All.
 * `error` is empty when it worked, and a sentence to show when it did not.
 */
export interface SessionControlOutcome {
  subscription_id: number;
  trader_uid?: string;
  error?: string;
  /** Why a session was left alone. Only on `skipped`, and written to be shown. */
  reason?: string;
}

/** The reply to Pause All or Stop All. `considered: 0` means nothing was running. */
export interface BulkSessionControlResult {
  action: 'paused' | 'stopped';
  considered: number;
  succeeded: number;
  failed: SessionControlOutcome[];
  changed: SessionControlOutcome[];
  /*
   * Sessions the button deliberately did not touch, and why (S23).
   *
   * Optional because a server that has not been redeployed yet will not send
   * it, and the page must not break on that -- every read below is guarded.
   */
  skipped?: SessionControlOutcome[];
}

export interface ReversedPosition {
  id: number;
  subscription_id: number;
  symbol: string;
  side: string;
  follows_side: string;
  exit_mode: 'own' | 'with_trader';
  opened_at: string;
}

export const subscriptionApi = {
  /*
   * Whether this account may use an AI strategy, and whether a model key is
   * stored. The SERVER decides eligibility -- the client asks rather than
   * checking an email list of its own, so the answer cannot be wrong in the
   * direction that matters.
   */
  getAIStatus: (): Promise<{
    available: boolean; configured?: boolean;
    provider?: string; model?: string; hint?: string; last_used_at?: string | null;
    // Present only for provider 'custom'. The settings page shows its host in
    // place of a key hint, because "custom" alone does not say which model is
    // trading -- and an endpoint that needs no key has no hint to show.
    base_url?: string | null;
  }> => fetchApi('/api/v1/replicator/ai/credentials'),

  /****************
   * THE AGENT: the other AI option, and the opposite direction again.
   *
   * getAIStatus above is the MODEL path -- the user gives us a provider key and
   * we assemble the prompt. This is the AGENT path: the user runs a service, we
   * POST the order to it, and everything it knows lives on their side.
   *
   * Registered once per account. Attaching it to a session is a separate call,
   * because turning it on for a live session is a decision about that session
   * and not about the connection.
   ****************/
  getAgent: (): Promise<{
    connected: boolean;
    endpoint?: string;
    label?: string;
    connected_at?: string;
    sessions_using_it?: number;
  }> => fetchApi('/api/v1/replicator/ai/agent'),

  // The secret comes back once per save. Like the MCP token it is not stored
  // here -- unlike the MCP token, the server DOES keep it, because the server
  // is what signs with it.
  connectAgent: (endpoint: string, label: string): Promise<{
    endpoint: string; label: string; secret: string;
    sessions_updated: number; note: string;
  }> => fetchApi('/api/v1/replicator/ai/agent', {
    method: 'POST',
    body: JSON.stringify({ endpoint, label }),
  }),

  disconnectAgent: (): Promise<{
    disconnected: boolean; sessions_detached: number; what_happens_next: string;
  }> => fetchApi('/api/v1/replicator/ai/agent', { method: 'DELETE' }),

  // enabled=false takes no terms version: withdrawing is never gated.
  // transport 'pull' means we park the order and their client collects it;
  // 'push' (default) means we call the endpoint registered on the account.
  setSessionAgent: (
    id: number,
    enabled: boolean,
    aiTermsVersion?: string,
    transport?: 'push' | 'pull',
  ): Promise<{ enabled: boolean; applies_to: string }> =>
    fetchApi(`/api/v1/replicator/subscriptions/${id}/agent`, {
      method: 'PUT',
      body: JSON.stringify({
        enabled,
        ai_terms_version: aiTermsVersion ?? '',
        ...(transport ? { transport } : {}),
      }),
    }),

  // The key is sent once and never comes back. Stored encrypted with the same
  // crypto the broker credentials use, because it is the same class of secret.
  // baseURL is sent only for provider 'custom' -- any OpenAI-compatible
  // endpoint, including a model the user hosts themselves.
  saveAIKey: (provider: string, model: string, apiKey: string, baseURL?: string): Promise<any> =>
    fetchApi('/api/v1/replicator/ai/credentials', {
      method: 'POST',
      body: JSON.stringify({ provider, model, api_key: apiKey, base_url: baseURL ?? '' }),
    }),

  // The context blocks a user may choose from, generated server-side from the
  // resolvers that exist so the screen cannot offer one that does not.
  getAIContextCatalogue: (): Promise<{
    available: boolean;
    blocks: {
      name: string; label: string; returns: string;
      params?: Record<string, string>; approx_tokens: string;
    }[];
    note?: string;
  }> => fetchApi('/api/v1/replicator/ai/context-catalogue'),

  // Change an existing session's context. Creation sends the spec inline
  // instead, so a session never runs an order on a prompt its owner did not
  // specify.
  /**
   * One session's AI settings.
   *
   * use_ai and ai_fallback are OMITTED unless they are being changed: the
   * server treats absent as "leave it alone", so editing the context blocks
   * cannot switch a session's model off by not mentioning it.
   */
  setAISessionSettings: (
    sessionId: number,
    body: {
      blocks?: unknown[];
      instructions?: string;
      use_ai?: boolean;
      ai_terms_version?: string;
      ai_fallback?: string;
    },
  ): Promise<any> =>
    fetchApi(`/api/v1/replicator/subscriptions/${sessionId}/ai-context`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  deleteAIKey: (): Promise<any> =>
    fetchApi('/api/v1/replicator/ai/credentials', { method: 'DELETE' }),

  /*
   * The AI Trader link (2026-09-26): what the user chose -- decision window,
   * standing decisions, what happens while the AI is away -- and the online
   * light. backend api/ai_session_link.go.
   */
  getAILink: (sessionId: number): Promise<AILink> =>
    fetchApi(`/api/v1/replicator/subscriptions/${sessionId}/ai-link`),

  /*
   * The AI session's directives (2026-10-03): how the user wants it to trade,
   * written by their AI after the audition, or by the user here. The AI
   * decides by them; CopyFi sets no limits of its own. backend
   * api/ai_directives.go.
   */
  getDirectives: (sessionId: number): Promise<{ directives: string }> =>
    fetchApi(`/api/v1/replicator/subscriptions/${sessionId}/directives`),
  putDirectives: (sessionId: number, directives: string): Promise<{ directives: string }> =>
    fetchApi(`/api/v1/replicator/subscriptions/${sessionId}/directives`, {
      method: 'PUT',
      body: JSON.stringify({ directives }),
    }),
  putAILink: (sessionId: number, body: Partial<{
    decision_window_sec: number;
    offline_close_after_min: number;
    acknowledge_24x7: boolean;
  }>): Promise<AILink> =>
    fetchApi(`/api/v1/replicator/subscriptions/${sessionId}/ai-link`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  /*
   * What the model decided, and what was done with it. The summary matters
   * more than any single row: how often it declines, and how often its answer
   * had to be cut to the session's limits.
   */
  getAIDecisions: (limit = 25): Promise<{
    available: boolean;
    decisions: any[];
    summary?: {
      returned: number; took: number; skipped: number;
      no_decision: number; clamped_to_your_limits: number; avg_latency_ms: number;
    };
    /* One per decision, in the same order as `decisions`. trade_id is empty
       when the trade could not be identified, and `unattributed` says why in
       words rather than leaving a blank the reader has to interpret. */
    outcomes?: Array<{
      decision_id: number;
      trade_id?: string;
      realized_pnl: number;
      win_loss?: string;
      status?: string;
      entry_time?: string;
      unattributed?: string;
    }>;
    /* Settled trades only. unattributed_takes is part of the result, not a
       footnote: a win rate over half the decisions must not be read as one
       over all of them. */
    results?: {
      decisions: number; took: number; skipped: number;
      settled_trades: number; still_open: number;
      wins: number; losses: number; realized_pnl: number;
      unattributed_takes: number;
    };
  }> => fetchApi(`/api/v1/replicator/ai/decisions?limit=${limit}`),

  create: (params: {
    credential_id: number; trader_uid: string; model_name?: string; model_config: any;
    starting_balance: number; delivery_mode?: string;
    use_ai?: boolean; ai_terms_version?: string;
    // JSON: the context blocks and instructions this session's model is given.
    // Inline with creation, so no order is ever decided on a prompt between the
    // session existing and its owner's choice being stored.
    ai_context_spec?: string;
    // "strategy" or "skip": what the session does when its model does not answer.
    ai_fallback?: string;
    /*
     * WHICH AI decides: "model" (the key on the account), "agent" (we POST to
     * the agent registered on the account) or "agent_pull" (their own client
     * collects the order over MCP).
     *
     * Sent at creation because it cannot be sent later. How a session is
     * decided is fixed for its whole life, so this is the only moment the
     * choice exists — PUT /subscriptions/{id}/agent answers 409.
     */
    ai_decider?: 'model' | 'agent' | 'agent_pull';
    /* The account's one entry in the $1,000 competition. The server then
       sets paper, $10,000 and a 10x cap itself, and locks the session. */
    competition?: boolean;
  }): Promise<any> =>
    fetchApi('/api/v1/replicator/subscriptions', {
      method: 'POST',
      body: JSON.stringify(params),
    }),

  getAll: (): Promise<any[]> =>
    fetchApi('/api/v1/replicator/subscriptions'),

  /** Link state for a session's Telegram delivery. Mints an invitation when it
   *  is not linked yet — there is no "save my username" call, because a bot may
   *  only message a chat the user opened first. */
  telegramLink: (sessionId: number | string): Promise<any> =>
    fetchApi(`/api/v1/replicator/subscriptions/${sessionId}/telegram`),

  telegramUnlink: (sessionId: number | string): Promise<any> =>
    fetchApi(`/api/v1/replicator/subscriptions/${sessionId}/telegram`, { method: 'DELETE' }),

  // pauseMode: which pause, when pausing with trades open (SessionControlDialog).
  updateStatus: (id: number, status: string, pauseMode?: 'keep_exits' | 'all'): Promise<any> =>
    fetchApi(`/api/v1/replicator/subscriptions/${id}`, {
      method: 'PUT',
      body: JSON.stringify(pauseMode ? { status, pause_mode: pauseMode } : { status }),
    }),

  /* A running session's settings (handlers.go, S21): the server applies them to
     its next orders and records the change in its lifecycle. Same trader, same
     saved strategy. Admin screen only for now (SessionApplySettings, 2026-11-03). */
  updateConfig: (id: number, modelConfig: unknown): Promise<{ status: string; applied?: string }> =>
    fetchApi(`/api/v1/replicator/subscriptions/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ modelConfig }),
    }),

  /****************
   * PAUSE ALL / STOP ALL.
   *
   * The reply is per session and the status code says what KIND of answer it
   * is: 200 everything, 207 some of it, 409 none. Stop All liquidates, so a
   * partial result is ordinary -- and reading one as plain success is how a
   * user ends up believing every position is closed while one is still open.
   *
   * CALLERS MUST RENDER `failed`. It carries a sentence per session that the
   * user can act on.
   ****************/
  pauseAll: (pauseMode?: 'keep_exits' | 'all'): Promise<BulkSessionControlResult> =>
    fetchApi('/api/v1/replicator/subscriptions/pause-all', {
      method: 'POST',
      ...(pauseMode ? { body: JSON.stringify({ pause_mode: pauseMode }) } : {}),
    }),

  stopAll: (): Promise<BulkSessionControlResult> =>
    fetchApi('/api/v1/replicator/subscriptions/stop-all', { method: 'POST' }),

  delete: (id: number): Promise<any> =>
    fetchApi(`/api/v1/replicator/subscriptions/${id}`, { method: 'DELETE' }),

  getOrders: (subscriptionId?: string, limit: number = 500): Promise<any[]> => {
    const params = new URLSearchParams();
    if (subscriptionId) params.set('subscription_id', subscriptionId);
    params.set('limit', String(limit));
    return fetchApi(`/api/v1/replicator/orders?${params.toString()}`);
  },

  /* One page of a session's orders, newest first, and where the next starts.
     total is every order before paging (X-Total-Count); nextCursor is null on
     the last page. The session page fetches them all (2026-10-08). */
  getOrdersPage: async (subscriptionId: string, opts?: { limit?: number; cursor?: string }):
    Promise<{ orders: any[]; total: number | null; nextCursor: string | null }> => {
    const params = new URLSearchParams({ subscription_id: subscriptionId, limit: String(opts?.limit ?? 500) });
    if (opts?.cursor) params.set('cursor', opts.cursor);
    let total: number | null = null;
    let nextCursor: string | null = null;
    const orders = await fetchApi<any[]>(`/api/v1/replicator/orders?${params.toString()}`, {
      onResponse: (r) => {
        const t = r.headers.get('X-Total-Count');
        if (t !== null && t !== '' && Number.isFinite(Number(t))) total = Number(t);
        nextCursor = r.headers.get('X-Next-Cursor');
      },
    });
    return { orders: orders || [], total, nextCursor };
  },

  getPositions: (subscriptionId?: string): Promise<any[]> =>
    fetchApi(`/api/v1/replicator/positions${subscriptionId ? `?subscription_id=${subscriptionId}` : ''}`),

  /**
   * The broker's open book, with each position annotated by session.
   *
   * The venue's positions are the list -- deliberately not our own
   * replicated_positions rows, which can miss a position the exchange really
   * holds. Where two sessions hold the same symbol and side, `sessions[]`
   * carries each one's share of the size and of the PnL.
   */
  /* A copied position the user reversed at their broker, attached to this
     session (2026-10-27), and their choice of what closes it. */
  reversedPositions: (subscriptionId: string | number): Promise<ReversedPosition[]> =>
    fetchApi(`/api/v1/replicator/subscriptions/${subscriptionId}/reversed`),
  setReversedExitMode: (subscriptionId: string | number, id: number, exitMode: 'own' | 'with_trader'): Promise<{ exit_mode: string }> =>
    fetchApi(`/api/v1/replicator/subscriptions/${subscriptionId}/reversed/${id}`, { method: 'PUT', body: JSON.stringify({ exit_mode: exitMode }) }),
  getOpenPositions: (kind: 'live' | 'paper' = 'live'): Promise<OpenPositionsResponse> =>
    fetchApi(kind === 'paper' ? '/api/v1/replicator/open-positions?kind=paper' : '/api/v1/replicator/open-positions'),

  /**
   * Every order RESTING on the exchange right now for the symbols this account
   * holds a position in -- including orders CopyFi did not place. M-65.
   *
   * `symbols_unanswered` is not decoration: a symbol listed there means the
   * exchange would not say what is resting on it, so an empty list for that
   * symbol is a gap in what we know and not a position without a stop.
   */
  getRestingOrders: (): Promise<RestingOrdersResponse> =>
    fetchApi('/api/v1/replicator/resting-orders'),

  /**
   * The signed-in user's equity over time, from hourly snapshots.
   *
   * `change_available` is false until a second snapshot exists. Read it before
   * rendering a change: until then there IS no change, and 0.00% would read as
   * "flat" rather than "not measured yet".
   */
  /** A display preference stored against the USER, so it follows them between
   *  the web app and the phone. Returns value: null when never set. */
  getPreference: (key: string): Promise<{ key: string; value: unknown }> =>
    fetchApi(`/api/v1/replicator/preferences?key=${encodeURIComponent(key)}`),

  putPreference: (key: string, value: unknown): Promise<{ saved: boolean }> =>
    fetchApi(`/api/v1/replicator/preferences`, {
      method: 'PUT',
      body: JSON.stringify({ key, value }),
    }),

  /** Direction, assets and leverage over CLOSED trades, for when nothing is open. */
  /** credentials: one broker's keys only (the Dashboard's broker tab); an empty list means none. */
  getHistoryExposure: (scope: 'live' | 'paper' = 'live', credentials?: number[]): Promise<any> =>
    fetchApi(`/api/v1/replicator/history-exposure?scope=${scope}${credentials ? `&credentials=${credentials.length ? credentials.join(',') : 'none'}` : ''}`),

  getEquityHistory: (
    range: '24h' | '7d' | '30d' | '90d' = '24h',
    scope: 'all' | 'live' | 'paper' | `key:${number}` = 'all',
  ): Promise<EquityHistoryResponse> =>
    fetchApi(`/api/v1/replicator/equity?range=${range}&scope=${scope}`),

  // Session performance — mirror of trader performance endpoint
  getSessionPerformance: (subscriptionId: string | number): Promise<any> =>
    fetchApi(`/api/v1/replicator/subscriptions/${subscriptionId}/performance`),

  // The recorded hourly wallet of the ONE key this session trades on, with the
  // hours nobody could read as gaps (key_wallet_history.go, 2026-10-01).
  getSessionWalletHistory: (subscriptionId: string | number): Promise<SessionWalletHistory> =>
    fetchApi(`/api/v1/replicator/subscriptions/${subscriptionId}/wallet-history`),

  // Session trades — assembled from replicated_orders grouped by trade_id
  getSessionTrades: (subscriptionId: string | number, status?: 'OPEN' | 'CLOSED', limit: number = 1000): Promise<any[]> => {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    params.set('limit', String(limit));
    return fetchApi(`/api/v1/replicator/subscriptions/${subscriptionId}/trades?${params.toString()}`);
  },

  /* One page of a session's closed trades (the first page also carries the
     ones closed at the broker), with the count of all of them and the cursor
     of the next page. 2026-10-08. */
  getSessionTradesPage: async (subscriptionId: string | number, opts?: { limit?: number; cursor?: string }):
    Promise<{ trades: any[]; total: number | null; nextCursor: string | null }> => {
    const params = new URLSearchParams({ status: 'CLOSED', limit: String(opts?.limit ?? 5000) });
    if (opts?.cursor) params.set('cursor', opts.cursor);
    let total: number | null = null;
    let nextCursor: string | null = null;
    const trades = await fetchApi<any[]>(`/api/v1/replicator/subscriptions/${subscriptionId}/trades?${params.toString()}`, {
      onResponse: (r) => {
        const t = r.headers.get('X-Total-Count');
        if (t !== null && t !== '' && Number.isFinite(Number(t))) total = Number(t);
        nextCursor = r.headers.get('X-Next-Cursor');
      },
    });
    return { trades: trades || [], total, nextCursor };
  },

  // Per-trade order lifecycle for a session — returns OPEN/INCREASE/DECREASE/CLOSE
  // shaped to match InferredOrderResponse so TradeDetailsModal can swap endpoints.
  getSessionTradeOrders: (subscriptionId: string | number, tradeId: string | number): Promise<{ orders: any[]; count: number; trade_id: string; timestamp: number }> =>
    fetchApi(`/api/v1/replicator/subscriptions/${subscriptionId}/trades/${tradeId}/orders`),

  /**
   * Close ONE contract on ONE session, leaving the session running.
   *
   * The server waits for the closing order to fill before answering, so this is slow by
   * design and a rejection means the position may still be open — never report a close
   * this did not return successfully.
   *
   * A contract shared by two sessions is closed by calling this once per session. There is
   * no merged form: the venue holds one position and cannot tell the legs apart, so a
   * single close would have to guess the split.
   */
  /*
   * side: the row's own, LONG or SHORT. A session can hold both sides of one
   * contract, as two rows; without the side the server closed both
   * (2026-10-02). It now refuses a close with no side while both are held.
   */
  /*
   * The user's own stop-loss / take-profit (price > 0 sets, 0 removes) and the
   * margin of a live isolated position, on one open position of one session
   * (replicator api/position_controls.go, 2026-10-22). Real orders, real money:
   * the server checks the session is the caller's.
   */
  positionProtection: (subscriptionId: string | number, symbol: string, side: string): Promise<PositionProtection> =>
    fetchApi(`/api/v1/replicator/subscriptions/${subscriptionId}/positions/${encodeURIComponent(symbol)}/protection?side=${encodeURIComponent(side)}`),
  setPositionProtection: (subscriptionId: string | number, symbol: string, body: { side: string; kind: 'stop_loss' | 'take_profit'; price: number }): Promise<{ result: ProtectionResult }> =>
    fetchApi(`/api/v1/replicator/subscriptions/${subscriptionId}/positions/${encodeURIComponent(symbol)}/protection`, { method: 'PUT', body: JSON.stringify(body) }),
  adjustPositionMargin: (subscriptionId: string | number, symbol: string, body: { side: string; delta: number }): Promise<{ result: MarginResult }> =>
    fetchApi(`/api/v1/replicator/subscriptions/${subscriptionId}/positions/${encodeURIComponent(symbol)}/margin`, { method: 'PUT', body: JSON.stringify(body) }),

  closePosition: (subscriptionId: string | number, symbol: string, side?: string, pct?: number): Promise<{ closed: string; subscription_id: number }> =>
    fetchApi(`/api/v1/replicator/subscriptions/${subscriptionId}/positions/${encodeURIComponent(symbol)}/close${closeSideQuery(side, pct)}`, {
      method: 'POST',
    }),

  /**
   * Cancel one order still waiting at the broker. The server asks the broker's
   * live book first: an order that already ended is reported (409) and nothing
   * is sent. A 502 means the broker refused and the order may still be waiting.
   */
  cancelOrder: (orderId: number): Promise<{ cancelled: number }> =>
    fetchApi(`/api/v1/replicator/orders/${orderId}/cancel`, { method: 'POST' }),

  /**
   * Cancel a strategy stop the broker booked under its own id. The server
   * re-reads the live book and refuses (409) unless the order is still there
   * and is one of the caller's strategy stops; 200 only once the book confirms.
   */
  cancelRestingOrder: (credentialId: number, symbol: string, orderId: string): Promise<{ cancelled: string }> =>
    fetchApi('/api/v1/replicator/resting-orders/cancel', {
      method: 'POST',
      body: JSON.stringify({ credential_id: credentialId, symbol, order_id: orderId }),
    }),
};

/****************
 * Models (Strategies) API — server-side persistence
 ****************/

export const modelsApi = {
  getAll: (): Promise<any[]> =>
    fetchApi('/api/v1/models'),

  create: (model: { name: string; description?: string; model_type: string; configuration: any; is_active?: boolean; overwrite?: boolean }): Promise<any> =>
    fetchApi('/api/v1/models', {
      method: 'POST',
      body: JSON.stringify(model),
    }),

  update: (id: number | string, updates: { name?: string; description?: string; configuration?: any; is_active?: boolean }): Promise<any> =>
    fetchApi(`/api/v1/models/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    }),

  delete: (id: number | string): Promise<any> =>
    fetchApi(`/api/v1/models/${id}`, { method: 'DELETE' }),
};

/****************
 * Backtests API. Admin-only until the backtester opens (Guillaume 2026-10-15),
 * so it lives under /api/v1/admin/; the server refuses anyone else. Types and
 * endpoints are shared with the phone (lib/backtest.core.ts, 2026-10-20).
 ****************/
export type {
  BacktestStatus, BacktestSummary, BacktestDataNotes, Backtest, BacktestTrade, BacktestOrder, BacktestSkip, BacktestRequest,
} from '@/lib/backtest.core';
export const backtestApi = makeBacktestApi(fetchApi as any);

/** The bell's notices beside its orders (11-03): a backtest's results are ready. */
export interface AppNotice {
  id: number;
  kind: string; // 'backtest_ready'
  ref_id: number;
  status?: string; // the backtest's 'done' or 'failed'
  title?: string;
  created_at: string;
  web: string;
  phone: string;
}
export const noticesApi = {
  list: (): Promise<{ notices: AppNotice[] }> => fetchApi('/api/v1/notices'),
};
/** The same endpoints for every signed-in user, their own runs only (BACKTEST_PUBLIC). The Get Started flow. */
export const userBacktestApi = makeBacktestApi(fetchApi as any, USER_BACKTESTS);

/****************
 * Utility functions
 ****************/

export function getApiBaseUrl(): string {
  return API_BASE;
}

export function getWebSocketUrl(): string {
  // If VITE_WS_URL is explicitly set, use it directly
  if (import.meta.env.VITE_WS_URL) {
    return `${import.meta.env.VITE_WS_URL}/ws`;
  }

  // If VITE_API_URL is set, derive WS URL from it (replace http(s) with ws(s))
  if (API_BASE) {
    const wsBase = API_BASE.replace(/^http/, 'ws');
    return `${wsBase}/ws`;
  }

  // app.copyfi.ai/ws proxies via the frontend nginx to backend:8080. The
  // legacy ws.copyfi.ai subdomain returns Cloudflare 522 (origin unreachable
  // from CF edge for that hostname) so we route everything through the same
  // app host instead. (2026-05-25)
  return `wss://app.copyfi.ai/ws`;
}

/** One AI session's link: the user's choices and whether the AI is there. */
export interface AILink {
  session_id: number;
  decision_window_sec: number;
  standing_enabled: boolean;
  offline_close_after_min: number;
  emergency_stop_margin_pct: number;
  acknowledged_24x7_at?: string;
  online: boolean;
  last_seen_at?: string;
  offline_since?: string;
  offline_reason?: string;
  has_agent: boolean;
  // Way A: the session's AI collects orders over MCP with a session token.
  pull?: boolean;
  standing_decisions_live: number;
}

/****************
 * THE COMMUNITY (2026-09-26). backend internal/api/rest/community.go.
 ****************/
export type CommunityKind = 'result' | 'strategy' | 'recipe' | 'question' | 'trader_talk' | 'asset_talk' | 'pros';

export interface CommunityProfile {
  handle: string;
  avatar: string;
  bio: string;
  show_dollars: boolean;
  compete: boolean;
}

export interface CommunityMemberProfile {
  handle: string; avatar: string; bio: string; joined_at: string; reputation: number;
  badges?: import('@/lib/engagement.core').Achievement[]; posts: CommunityPost[]; can_message: boolean; blocked: boolean; me: boolean;
}

export interface CommunityPost {
  id: number;
  kind: CommunityKind;
  title: string;
  body: string;
  trader_uid?: string;
  /** The trader's on-screen name, from the server. Never show trader_uid. */
  trader_name?: string;
  /** Asset talk: the asset (/assets/:slug) and its ticker. */
  asset_slug?: string;
  asset_name?: string;
  strategy_name?: string;
  answered_by?: number;
  created_at: string;
  author: string;
  avatar: string;
  author_reputation: number;
  badges?: import('@/lib/engagement.core').Achievement[];
  votes: number;
  my_vote: boolean;
  comments: number;
  /** The three most-voted comments, shown under the post (10-25). */
  top_comments?: CommunityComment[];
  result?: {
    label: 'LIVE' | 'PAPER';
    closed_trades: number;
    win_rate: number;
    return_pct?: number;
    net_pnl?: number;
    days: number;
    strategy: string;
  };
  recipe?: { model?: string; sources?: string; rules?: string };
  has_strategy: boolean;
  mine: boolean;
  not_advice: string;
}

export interface CommunityComment {
  id: number;
  post_id: number;
  /** The comment this one answers; absent = an answer to the post. */
  parent_id?: number;
  body: string;
  created_at: string;
  author: string;
  avatar: string;
  badges?: import('@/lib/engagement.core').Achievement[];
  votes: number;
  my_vote: boolean;
  mine: boolean;
}

/** One line of the chat dock (rest/community_chat.go). */
export interface ChatSummary { kind: 'dm' | 'group'; handle?: string; group?: number; name: string; last: string; unread: number }

export const communityApi = {
  // A speed run sees the Pros lock as a newcomer does, admins included (round 9).
  me: (): Promise<{ profile: CommunityProfile | null; reputation: number; banned: boolean; member: boolean; pro?: boolean; pro_golds?: number; pro_golds_needed?: number }> =>
    fetchApi<{ profile: CommunityProfile | null; reputation: number; banned: boolean; member: boolean; pro?: boolean; pro_golds?: number; pro_golds_needed?: number }>('/api/v1/community/me').then((m) => (inSpeedRun() ? { ...m, pro: false, pro_golds: 0 } : m)),
  saveMe: (p: Partial<CommunityProfile>): Promise<CommunityProfile> =>
    fetchApi('/api/v1/community/me', { method: 'PUT', body: JSON.stringify(p) }),
  /* A page of the feed. has_more says another exists; next_before (newest
     first) or next_offset (Top) is where it starts. 2026-10-08. */
  posts: (q: { kind?: string; trader_uid?: string; asset_slug?: string; sort?: 'new' | 'top'; before?: number; offset?: number }):
    Promise<{ posts: CommunityPost[]; has_more?: boolean; next_before?: number; next_offset?: number }> => {
    const p = new URLSearchParams();
    Object.entries(q).forEach(([k, v]) => { if (v !== undefined && v !== '') p.set(k, String(v)); });
    return fetchApi(`/api/v1/community/posts?${p.toString()}`);
  },
  post: (id: number): Promise<{ post: CommunityPost; comments: CommunityComment[] }> =>
    fetchApi(`/api/v1/community/posts/${id}`),
  create: (body: {
    kind: CommunityKind; title: string; body?: string; trader_uid?: string; asset_slug?: string;
    session_id?: number; strategy_id?: number; recipe?: { model: string; sources: string; rules: string };
    /** Result posts: show the dollar amount too. Asked per post. */
    show_dollars?: boolean;
  }): Promise<{ id: number }> =>
    fetchApi('/api/v1/community/posts', { method: 'POST', body: JSON.stringify(body) }),
  remove: (id: number): Promise<unknown> => fetchApi(`/api/v1/community/posts/${id}`, { method: 'DELETE' }),
  comment: (id: number, body: string, parentId?: number): Promise<{ id: number }> =>
    fetchApi(`/api/v1/community/posts/${id}/comments`, { method: 'POST', body: JSON.stringify({ body, parent_id: parentId }) }),
  removeComment: (id: number): Promise<unknown> => fetchApi(`/api/v1/community/comments/${id}`, { method: 'DELETE' }),
  /** A member's profile from their name (round 8 #237): badges, newest posts, message/block state. */
  member: (handle: string): Promise<CommunityMemberProfile> =>
    fetchApi(`/api/v1/community/members/${encodeURIComponent(handle)}`),
  members: (q: string): Promise<{ members: { handle: string; can_message: boolean }[] }> =>
    fetchApi(`/api/v1/community/members?q=${encodeURIComponent(q)}`),
  vote: (target: 'post' | 'comment', id: number, up: boolean): Promise<unknown> =>
    fetchApi('/api/v1/community/vote', { method: 'POST', body: JSON.stringify({ target, id, up }) }),
  answered: (postId: number, commentId: number): Promise<unknown> =>
    fetchApi(`/api/v1/community/posts/${postId}/answered`, { method: 'POST', body: JSON.stringify({ comment_id: commentId }) }),
  saveStrategy: (postId: number): Promise<{ saved: boolean; name: string }> =>
    fetchApi(`/api/v1/community/posts/${postId}/save-strategy`, { method: 'POST' }),
  summary: (postId: number): Promise<{ summary: string }> =>
    fetchApi(`/api/v1/community/posts/${postId}/summary`, { method: 'POST' }),
  report: (target: 'post' | 'comment' | 'dm', id: number, reason: string): Promise<unknown> =>
    fetchApi('/api/v1/community/report', { method: 'POST', body: JSON.stringify({ target, id, reason }) }),
  // Blocking a member (backend community_block.go): their posts, replies and
  // messages stop reaching you. They are not told.
  blocked: (): Promise<{ blocked: { handle: string; avatar: string; blocked_at: string }[] }> =>
    fetchApi('/api/v1/community/blocks'),
  block: (handle: string): Promise<{ blocked: boolean; handle: string }> =>
    fetchApi('/api/v1/community/blocks', { method: 'POST', body: JSON.stringify({ handle }) }),
  unblock: (handle: string): Promise<{ blocked: boolean; handle: string }> =>
    fetchApi(`/api/v1/community/blocks/${encodeURIComponent(handle)}`, { method: 'DELETE' }),
  similar: (q: string): Promise<{ posts: CommunityPost[] }> =>
    fetchApi(`/api/v1/community/similar?q=${encodeURIComponent(q)}`),
  topHelpers: (): Promise<{ helpers: { handle: string; avatar: string; reputation: number }[] }> =>
    fetchApi('/api/v1/community/top-helpers'),
  competition: (month?: string): Promise<{
    month: string; rules: string;
    live: { handle: string; avatar: string; return_pct: number; closed_trades: number }[];
    paper: { handle: string; avatar: string; return_pct: number; closed_trades: number }[];
  }> => fetchApi(`/api/v1/community/competition${month ? `?month=${month}` : ''}`),
  dmThreads: (): Promise<{ member: boolean; threads: { handle: string; last: string }[] }> =>
    fetchApi('/api/v1/community/dm'),
  /* The newest messages before `before`, in reading order; has_more /
     next_before lead to the older ones. 2026-10-08. */
  dmThread: (handle: string, before?: number): Promise<{ with: string; messages: { id: number; body: string; created_at: string; mine: boolean; warning?: string }[]; has_more?: boolean; next_before?: number }> =>
    fetchApi(`/api/v1/community/dm/${encodeURIComponent(handle)}${before ? `?before=${before}` : ''}`),
  sendDM: (handle: string, body: string): Promise<{ id: number }> =>
    fetchApi(`/api/v1/community/dm/${encodeURIComponent(handle)}`, { method: 'POST', body: JSON.stringify({ body }) }),
  /* Chat boxes (rest/community_chat.go, 2026-10-24): the dock, read marks, group chats. */
  chatList: (): Promise<{ member: boolean; chats: ChatSummary[] }> => fetchApi('/api/v1/community/chat'),
  dmRead: (handle: string, id: number): Promise<unknown> =>
    fetchApi(`/api/v1/community/dm/${encodeURIComponent(handle)}/read`, { method: 'POST', body: JSON.stringify({ id }) }),
  createGroup: (name: string, handles: string[]): Promise<{ id: number; name: string }> =>
    fetchApi('/api/v1/community/groups', { method: 'POST', body: JSON.stringify({ name, handles }) }),
  groupThread: (id: number): Promise<{ id: number; name: string; members: string[]; messages: { id: number; body: string; author: string; mine: boolean; warning?: string; created_at: string }[] }> =>
    fetchApi(`/api/v1/community/groups/${id}`),
  sendGroup: (id: number, body: string): Promise<{ id: number }> =>
    fetchApi(`/api/v1/community/groups/${id}`, { method: 'POST', body: JSON.stringify({ body }) }),
  leaveGroup: (id: number): Promise<unknown> => fetchApi(`/api/v1/community/groups/${id}/leave`, { method: 'POST' }),
  adminReports: (): Promise<{ reports: { id: number; target: string; target_id: number; reason: string; text: string; author: string; created_at: string }[] }> =>
    fetchApi('/api/v1/admin/community/reports'),
  /** An admin takes down any post, comment or message (kept as evidence). */
  adminRemove: (target: 'post' | 'comment' | 'dm', id: number, banDays = 0): Promise<unknown> =>
    fetchApi('/api/v1/admin/community/remove', { method: 'POST', body: JSON.stringify({ target, id, ban_days: banDays }) }),
  moderate: (reportId: number, action: 'hide' | 'dismiss', banDays = 0): Promise<unknown> =>
    fetchApi('/api/v1/admin/community/moderate', { method: 'POST', body: JSON.stringify({ report_id: reportId, action, ban_days: banDays }) }),
};

/** Beta feedback (2026-09-26). backend internal/api/rest/feedback.go. */
export const feedbackApi = {
  send: (area: 'advisor' | 'trader' | 'community', rating: number, text: string): Promise<unknown> =>
    fetchApi('/api/v1/feedback', { method: 'POST', body: JSON.stringify({ area, rating, text }) }),
  list: (): Promise<{ feedback: { id: number; area: string; rating: number; text: string; created_at: string; who: string; user_id: string }[] }> =>
    fetchApi('/api/v1/admin/feedback'),
};

/**
 * Which brokers this account sees (replicator api/brokers.go, 2026-10-11).
 * Preview ones only reach preview users. Opening a broker is one server move,
 * and every screen that names brokers follows this list.
 */
export interface BrokerView { id: string; name: string; preview: boolean }
export const brokersApi = {
  list: (): Promise<{ brokers: BrokerView[] }> => fetchApi('/api/v1/replicator/brokers'),
};

/****************
 * Engagement (2026-10-21): trader picks, visits, the recap, milestones and
 * push preferences. Shapes and paths: lib/engagement.core.ts (twin of the
 * phone's), backend rest/trader_picks.go, rest/engagement.go, webpush/events.go.
 ****************/
export const engagementApi = {
  picks: (venue?: string): Promise<{ picks: TraderPicks }> =>
    fetchApi(`${ENGAGEMENT_PATHS.picks}${venue ? `?venue=${encodeURIComponent(venue)}` : ''}`),
  visit: (): Promise<Visit> => fetchApi(ENGAGEMENT_PATHS.visit, { method: 'POST' }),
  recap: (since?: string | null): Promise<Recap> =>
    fetchApi(`${ENGAGEMENT_PATHS.recap}${since ? `?since=${encodeURIComponent(since)}` : ''}`),
  milestones: (): Promise<MilestoneState> => fetchApi(ENGAGEMENT_PATHS.milestones),
  celebrated: (key: string): Promise<unknown> => fetchApi(ENGAGEMENT_PATHS.celebrated(key), { method: 'POST' }),
  // The push routes verify the Clerk session themselves (rest/push_subscriptions.go).
  quiz: (): Promise<{ quizzes: QuizState[] }> => fetchApi(ENGAGEMENT_PATHS.quiz),
  achievements: (): Promise<import('@/lib/engagement.core').AchievementState> => fetchApi(ENGAGEMENT_PATHS.achievements),
  saveAchievements: (body: { showcase?: string[]; goal?: string; goals?: string[] }): Promise<{ showcase: string[]; goal: string }> =>
    fetchApi(ENGAGEMENT_PATHS.achievements, { method: 'PUT', body: JSON.stringify(body) }),
  quizResult: (body: { quiz: string; score: number; total: number }): Promise<{ perfect: boolean; first_perfect: boolean }> =>
    fetchApi(ENGAGEMENT_PATHS.quiz, { method: 'POST', body: JSON.stringify(body) }),
  reviewAsk: (moment: string): Promise<{ ask: boolean }> => fetchApi(`${ENGAGEMENT_PATHS.review}?moment=${encodeURIComponent(moment)}`),
  review: (body: { moment: string; rating?: number; comment?: string }): Promise<unknown> =>
    fetchApi(ENGAGEMENT_PATHS.review, { method: 'POST', body: JSON.stringify(body) }),
  pushPreferences: (token: string): Promise<{ preferences: PushPreference[] }> =>
    fetchApi(ENGAGEMENT_PATHS.pushPreferences, { headers: { Authorization: `Bearer ${token}` } }),
  setPushPreference: (token: string, p: PushPreference): Promise<{ preferences: PushPreference[] }> =>
    fetchApi(ENGAGEMENT_PATHS.pushPreferences, { method: 'PUT', body: JSON.stringify(p), headers: { Authorization: `Bearer ${token}` } }),
};

export interface PushPreference { kind: string; enabled: boolean; hour_utc?: number | null }

/****************
 * THE $1,000 PAPER-TRADING COMPETITION (backend rest/competition.go).
 * A session is subscriptionApi.create / groupSessionsApi.create with
 * competition: true; it runs on the account's competition wallet.
 ****************/
export interface CompetitionEntry {
  id: number; session_id: number; joined_at: string; house: boolean; disqualified: boolean;
  final_equity?: number; final_at?: string;
}
export interface CompetitionStanding {
  rank: number; entry_id: number; name: string;
  /** Admin view only (2026-11-03): the public ranking does not say which rows are CopyFi's. */
  house?: boolean; prize_eligible?: boolean;
  equity: number; return_pct: number; max_drawdown_pct: number; open_positions: number; final: boolean;
  joined_at: string;
  /** Sessions this wallet has run, and whether one is running now. */
  sessions?: number; running?: boolean;
  /** Registered, no competition session yet (entry_id 0, no profile). */
  not_started?: boolean;
  // Admin only.
  user_id?: string; session_id?: number; trader_uid?: string; use_ai?: boolean; status?: string;
  disqualified?: boolean; disqualify_reason?: string;
}
/** One session of a competition wallet. */
export interface CompetitionRun {
  id: number; session_id: number; start_capital: number; started_at: string; ended_at?: string; end_capital?: number;
}
export interface CompetitionInfo {
  starts_at: string; ends_at: string; now: string; phase: 'upcoming' | 'open' | 'ended';
  prize_usd: number; starting_balance: number;
  leaderboard_public: boolean; video_url: string; participants: number | null;
  /** May this account see the ranking and the profiles (participants once it opens). */
  can_view_ranking: boolean;
  registrants: number | null; registered: boolean;
  /** can_enter: may start a competition session now. */
  is_admin: boolean; can_enter: boolean; entry: CompetitionEntry | null; standing?: CompetitionStanding;
  /** The wallet's running session, and what the next one starts with. */
  running: CompetitionRun | null; next_capital: number;
  /** The public name (Community handle); '' when none yet. */
  handle: string;
  /** Every competition session of this account, stopped ones too. */
  session_ids?: number[];
}
export interface CompetitionProfileSession {
  session_id: number; name: string; start_capital: number; end_capital?: number; equity: number; return_pct: number;
  started_at: string; ended_at?: string; running: boolean;
}
export interface CompetitionProfile {
  entry_id: number; name: string; own: boolean; standing: CompetitionStanding | null; starting_balance: number;
  sessions: CompetitionProfileSession[]; curve: { at: string; equity: number }[];
  /** The rows a session page reads (store.UserTrade), every session of the wallet. */
  trades: any[]; open_trades: any[];
  /** The session page's table rows (replicatorapi.SessionOpenTrades / SessionClosedTrades),
   *  every competition session, newest first. Absent from a server before 2026-10-31. */
  open_rows?: any[]; closed_rows?: any[];
  closed_trades: number; wins: number; win_rate: number; joined_at: string;
}
export interface CompetitionSettings {
  starts_at: string; ends_at: string; leaderboard_public: boolean; video_url: string; updated_at?: string;
}
export const competitionApi = {
  info: (): Promise<CompetitionInfo> => fetchApi('/api/v1/competition'),
  leaderboard: (): Promise<{ public: boolean; phase: string; rows: CompetitionStanding[]; rules: string; my_entry_id: number }> =>
    fetchApi('/api/v1/competition/leaderboard'),
  register: (): Promise<{ registered: boolean }> => fetchApi('/api/v1/competition/register', { method: 'POST' }),
  nickname: (handle: string): Promise<{ handle: string }> =>
    fetchApi('/api/v1/competition/nickname', { method: 'PUT', body: JSON.stringify({ handle }) }),
  profile: (entryId: number | string): Promise<CompetitionProfile> => fetchApi(`/api/v1/competition/profile/${entryId}`),
  /** One trade's orders on a profile, as /subscriptions/{id}/trades/{trade}/orders shapes them, for whoever may see it. */
  tradeOrders: (entryId: number | string, tradeId: number | string): Promise<{ orders: any[]; count: number; trade_id: string; timestamp: number }> =>
    fetchApi(`/api/v1/competition/profile/${entryId}/trades/${encodeURIComponent(String(tradeId))}/orders`),
  admin: (): Promise<{ settings: CompetitionSettings; phase: string; rows: CompetitionStanding[] }> =>
    fetchApi('/api/v1/admin/competition'),
  saveSettings: (body: Partial<CompetitionSettings>): Promise<CompetitionSettings> =>
    fetchApi('/api/v1/admin/competition/settings', { method: 'PUT', body: JSON.stringify(body) }),
  setEntry: (id: number, body: { house?: boolean; disqualified?: boolean; reason?: string }): Promise<unknown> =>
    fetchApi(`/api/v1/admin/competition/entries/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  equity: (id: number): Promise<{ entry_id: number; points: { at: string; equity: number }[] }> =>
    fetchApi(`/api/v1/admin/competition/entries/${id}/equity`),
};
